import React, {
    ForwardedRef,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState,
    useRef,
} from 'react';
import {
    InputCheckbox,
    InputSocialSecurity,
} from '../../../component-library/index';
import SessionStorage from '../../../utils/SessionStorage';
import { APP_VERIFICATION_EVENT, IApplicationVerificationElement } from '../ApplicationVerification';
import { getAcquisitionFlow, getAppData, homeAddressCheckRegex, getFormattedDate, areStringsEqual, formatDateOfBirth, AcquisitionFlow, isDateValid } from '../../../utils/acquisition-utils';
import { isValidEmailFormat, regexValidation } from '../../../utils/cob-utils';
import './PersonalInformation.css';
import useLoqate from '../../../hooks/useLoqate';
import useBriteVerify from '../../../hooks/useBriteVerify';
import { useAcquisitionContext } from '../../../providers/AcquisitionProvider/AcquisitionProvider';
import NameField from '../../FieldContainers/NameField/NameField';
import PhoneNumberField from '../../FieldContainers/PhoneNumberField/PhoneNumberField';
import { MiddleInitialField } from '../../FieldContainers/MiddleInitialField/MiddleInitialField';
import EmailField from '../../FieldContainers/EmailField/EmailField';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import DateOfBirthField from '../../FieldContainers/DateOfBirthField/DateOfBirthField';
import SsnField from '../../FieldContainers/SsnField/SsnField';
import AddressField from '../../FieldContainers/AddressFields/AddressField/AddressField';
import ApartmentField from '../../FieldContainers/AddressFields/ApartmentField/ApartmentField';
import CityField from '../../FieldContainers/AddressFields/CityField/CityField';
import StateField from '../../FieldContainers/AddressFields/StateField/StateField';
import ZipCodeField from '../../FieldContainers/AddressFields/ZipCodeField/ZipCodeField';
import SuffixField from '../../FieldContainers/SuffixField/SuffixField';
import { Row, Col } from '../../../importCL';
import MonthlyIncomeField from '../../FieldContainers/MonthlyIncomeField/MonthlyIncomeField';
import { useFeatureFlags } from '../../../providers/FeatureFlagProvider/FeatureFlagProvider';
import NameFieldWithProvider from '../../FieldContainers/NameField/NameFieldWithProvider';
import useAcquisitionProviderFacade from '../../../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade';
import MiddleInitialFieldWithProvider from '../../FieldContainers/MiddleInitialField/MiddleInitialFieldWithProvider';
import SuffixFieldWithProvider from '../../FieldContainers/SuffixField/SuffixFieldWithProvider';
import EmailFieldWithProvider from '../../FieldContainers/EmailField/EmailFieldWithProvider';
import { generateFieldErrorMessage } from '../../../utils/error-message';
import ReactHtmlParser from 'react-html-parser';
import PhoneNumberFieldWithProvider from '../../FieldContainers/PhoneNumberField/PhoneNumberFieldWithProvider';
import { TErrorType } from '../../../providers/ModernAcquisitionProvider/types';
import { DateOfBirthFieldWithProvider } from '../../FieldContainers/DateOfBirthField/DateOfBirthFieldWithProvider';
import { IPersonalInfo } from '../../../providers/AcquisitionProvider/models';

interface IPersonalInformationProps {
    lockTopSection?: string;
    lockBottomSection?: string;
    phoneLabel?: string;
    incomeLabel?: string;
    homeAddressLabel?: string;
    homeAddressNote?: string;
    ssnLabel?: string;
    dobLabel?: string;
    mailingAddressLabel?: string;
    nameLabel?: string;
    emailLabel?: string;
    lockEmail?: string;
    emailDisclaimer?: string;
    sectionTitle?: string;
    aptUnitPoBox?: boolean;
    partnerHostedVersion?: string;
    isZoomedIn?: boolean;
}

export const PersonalInformation = forwardRef((props: IPersonalInformationProps, ref: ForwardedRef<IApplicationVerificationElement>): JSX.Element => {
    PersonalInformation.displayName = 'PersonalInformation';
    const { getFeatureFlags } = useFeatureFlags();
    const { getPersonalInformation, personalInformationWrite, setIsHomeAddressShown, getPersonalInformationDob, getAuthorizedUserDob } = useAcquisitionProviderFacade();
    const regexContainer = regexValidation();
    const [topInEditMode, setTopInEditMode] = useState<boolean>(false);
    const [bottomInEditMode, setBottomInEditMode] = useState<boolean>(false);
    const [ssn, setSsn] = useState<string>('');
    const [showIcon, setShowIcon] = useState<boolean>(false);
    const [elist] = useState<String[]>(PathUtils.getMetaPropertyValue('cq:elist')?.split(',').map(email => atob(email)) ?? []);
    const [plist] = useState<String[]>(PathUtils.getMetaPropertyValue('cq:plist')?.split(',').map(phone => atob(phone)) ?? []);
    const [ssnValid, setSsnValid] = useState<boolean>(true);
    //this is needed for inputs not using a container that it handles it.
    //Needed for not showing the check when editing in verification
    const [isInputBlurred, setIsInputBlurred] = useState<Record<string, boolean>>({
        ssn: false,
        income: false,
    });

    const ssnRef = useRef<HTMLInputElement>(null);
    const stateDropdownRef = useRef<HTMLElement>(null);
    const homeStateDropdownRef = useRef<HTMLElement>(null);
    const addressRef = useRef<HTMLElement>(null);
    const aptRef = useRef<HTMLElement>(null);
    const cityRef = useRef<HTMLElement>(null);
    const zipRef = useRef<HTMLElement>(null);
    const homeAddressRef = useRef<HTMLElement>(null);
    const homeCityRef = useRef<HTMLElement>(null);
    const homeZipRef = useRef<HTMLElement>(null);
    const incomeRef = useRef<HTMLElement>(null);

    const {
        personalInfoForm, setPersonalInfoForm,
        setIsSamePersonError, authorizedUserInfo,
        setIsAuthorisedUserSectionValid, setisPersonalInfoInEditMode,
        setPersonalInfoErrors,
    } = useAcquisitionContext();

    const [lastEmail, setLastEmail] = useState<{ value: string, result: boolean }>({ value: '', result: false });
    const [lastPhone, setLastPhone] = useState<{ value: string, result: boolean }>({ value: '', result: false });
    const { emailValidationApi, phoneValidationApi } = useBriteVerify();
    const { initAutoComplete, setBasicListeners, getAddressData, pca, loqateEnabled } = useLoqate();
    const [homeControl, setHomeControl] = useState<any>();
    const [mailControl, setMailControl] = useState<any>();

    // Home as primary AB test-----------------------------------------------------------------
    type TPrimaryHomeABInfo = {
        homeAddressInputText: string;
        mailingAddressCheckboxText: string;
    }
    const primaryHomeABInfo: TPrimaryHomeABInfo | null = SessionStorage.get('primaryHomeABInfo')
        ? JSON.parse(SessionStorage.get('primaryHomeABInfo') as string) : null;
    const mailingCheckboxRef = useRef<boolean>(false);
    const [mailingCheckboxSelected, setMailingCheckboxSelected] = useState<boolean>(false);

    useEffect(() => {
        if (pca && loqateEnabled) {
            if (!mailControl) {
                setMailControl(initAutoComplete('PersonalInformation__addressLine1',
                    'PersonalInformation__addressLine2',
                    'PersonalInformation__addressCity',
                    'PersonalInformation__addressState',
                    'PersonalInformation__addressZip'));
            }

            if (!homeControl && personalInfoForm.homeAddressShown) {
                setHomeControl(initAutoComplete('PersonalInformation__homeAddressLine1',
                    'PersonalInformation__homeAddressLine2',
                    'PersonalInformation__homeAddressCity',
                    'PersonalInformation__homeAddressState',
                    'PersonalInformation__homeAddressZip'));
            }

            if (homeControl) {
                homeControl.load();
            }

            if (!personalInfoForm.homeAddressShown && !primaryHomeABInfo) {
                setPersonalInfoForm((prev: any) => ({
                    ...prev,
                    homeAddress: { value: '', valid: true },
                    homeAptUnit: { value: '', valid: true },
                    homeCity: { value: '', valid: true },
                    homeState: { value: '', valid: true },
                    homeZip: { value: '', valid: true },
                }));
            }
        }

    }, [topInEditMode, personalInfoForm.homeAddressShown]);

    useEffect(() => {
        if (topInEditMode && homeControl && personalInfoForm.homeAddressShown) {
            homeControl.load();

            setBasicListeners(homeControl);

            homeControl.listen('populate', (loqateAddress: any) => {
                setPersonalInfoForm((prev: any) => ({
                    ...prev,
                    homeAddress: { value: loqateAddress.FormattedLine1, valid: true },
                    homeAptUnit: { value: loqateAddress.SubBuilding || '', valid: true },
                    homeCity: { value: loqateAddress.City, valid: true },
                    homeState: { value: loqateAddress.Province, valid: true },
                    homeZip: { value: loqateAddress.PostalCode, valid: true },
                }));
                setPersonalInfoErrors((prev: any) => ({
                    ...prev,
                    HomeAddressLine1: 'None',
                    HomeCity: 'None',
                    HomeState: 'None',
                    HomeZipCode: 'None',
                }));

                setTimeout(() => {
                    // ensure change event on stateDropdown is triggered on loqate autofill
                    if (homeStateDropdownRef.current) {
                        homeStateDropdownRef.current.dispatchEvent(new Event('change', { 'bubbles': true }));
                    }
                }, 0);
            });
        }
    }, [homeControl]);

    useEffect(() => {
        if (topInEditMode && mailControl) {
            mailControl.load();
            setBasicListeners(mailControl);

            mailControl.listen('populate', (loqateAddress: any) => {
                setPersonalInfoForm((personalInfoForm: any) => ({
                    ...personalInfoForm,
                    mailingAddress: { value: loqateAddress.FormattedLine1, valid: true },
                    mailingAptUnit: { value: loqateAddress.SubBuilding || '', valid: true },
                    mailingCity: { value: loqateAddress.City, valid: true },
                    mailingState: { value: loqateAddress.Province, valid: true },
                    mailingZip: { value: loqateAddress.PostalCode, valid: true },
                }));

                const isPO = homeAddressCheckRegex.test(personalInfoForm.mailingAddress.value) || (!!props.aptUnitPoBox && homeAddressCheckRegex.test(personalInfoForm.mailingAptUnit.value));

                setPersonalInfoForm((prev: any) => ({ ...prev, homeAddressShown: isPO }));

                if (addressRef.current) {
                    refreshAddressState(addressRef.current);
                }
                if (aptRef.current) {
                    refreshAddressState(aptRef.current);
                }
                if (cityRef.current) {
                    refreshAddressState(cityRef.current);
                }
                if (zipRef.current) {
                    refreshAddressState(zipRef.current);
                }
                if (stateDropdownRef.current) {
                    refreshAddressState(stateDropdownRef.current);
                }

                setTimeout(() => {
                    // ensure change event on stateDropdown is triggered on loqate autofill
                    if (stateDropdownRef.current) {
                        stateDropdownRef.current.dispatchEvent(new Event('change', { 'bubbles': true }));
                    }
                }, 0);
            });
        }
    }, [mailControl]);

    useEffect(() => {
        getAppData().then(appData => {
            // PHA cannot edit address
            if (getAcquisitionFlow() === AcquisitionFlow.PHA || (!appData.PersonalInformation && !appData.Applicant)) {
                return;
            }

            let originalAddress;
            if (getAcquisitionFlow() === AcquisitionFlow.PA) {
                originalAddress = appData.Applicant.MailingAddress.AddressLineOne;
            } else {
                originalAddress = appData.PersonalInformation.MailingAddressLine1;
            }

            if (originalAddress !== personalInfoForm.mailingAddress.value && !loqateEnabled) {
                if (homeAddressCheckRegex.test(personalInfoForm.mailingAddress.value ?? '')) {
                    setPersonalInfoForm((prev: any) => ({ ...prev, homeAddressShown: true }));
                }
                checkAptUnitPoBox(personalInfoForm.mailingAptUnit.value ?? '', 'mailing');
            }
        });
    }, [personalInfoForm.mailingAddress, personalInfoForm.mailingAptUnit, personalInfoForm.mailingCity, personalInfoForm.mailingState, personalInfoForm.mailingZip]);

    const refreshAddressState = (element: HTMLElement) => {
        element.dispatchEvent(new Event('focus'));
        element.dispatchEvent(new Event('blur'));
    };

    const handleClickEdit = useCallback(async (evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        const target = evt.target as Element;
        const acquisitionData = getPersonalInformation();
        if (target.classList.contains('topSection')) {
            if (!topInEditMode) {
                setTopInEditMode(true);
                setisPersonalInfoInEditMode(true);
                if (primaryHomeABInfo) {
                    if (personalInfoForm.homeAddress.value === '') {
                        setPersonalInfoForm((prev: any) => ({
                            ...prev,
                            homeAddress: { value: personalInfoForm.mailingAddress.value, valid: true },
                            homeAptUnit: { value: personalInfoForm.mailingAptUnit.value, valid: true },
                            homeCity: { value: personalInfoForm.mailingCity.value, valid: true },
                            homeState: { value: personalInfoForm.mailingState.value, valid: true },
                            homeZip: { value: personalInfoForm.mailingZip.value, valid: true },
                            homeAddressShown: true,

                            mailingAddress: { value: '', valid: true },
                            mailingAptUnit: { value: '', valid: true },
                            mailingCity: { value: '', valid: true },
                            mailingState: { value: '', valid: true },
                            mailingZip: { value: '', valid: true },
                        }));
                    } else {
                        mailingCheckboxRef.current = true;
                        setMailingCheckboxSelected(true);
                    }

                }
            } else if (await validateTopForm()) {
                if (primaryHomeABInfo && personalInfoForm.mailingAddress.value === '') {
                    setPersonalInfoForm((prev) => ({
                        ...prev,
                        mailingAddress: { value: personalInfoForm.homeAddress.value, valid: true },
                        mailingAptUnit: { value: personalInfoForm.homeAptUnit.value, valid: true },
                        mailingCity: { value: personalInfoForm.homeCity.value, valid: true },
                        mailingState: { value: personalInfoForm.homeState.value, valid: true },
                        mailingZip: { value: personalInfoForm.homeZip.value, valid: true },
                        homeAddressShown: false,

                        homeAddress: { value: '', valid: true },
                        homeAptUnit: { value: '', valid: true },
                        homeCity: { value: '', valid: true },
                        homeState: { value: '', valid: true },
                        homeZip: { value: '', valid: true },
                    }));
                    mailingCheckboxRef.current = false;
                }
                const appData = await getAppData();
                const dob = getPersonalInformationDob();
                if (getAcquisitionFlow() === AcquisitionFlow.PHA) {
                    // Can't edit PII in PHA
                    return;
                } else if (getAcquisitionFlow() === AcquisitionFlow.PA) {
                    appData.Applicant = {
                        ...appData.Applicant,
                        ApplicantName: {
                            ...appData.Applicant.ApplicantName,
                            FirstName: acquisitionData?.firstName?.value,
                            MiddleInitial: personalInfoForm.middleInitial.value,
                            LastName: acquisitionData?.lastName?.value,
                            NameSuffix: personalInfoForm.suffix.value,
                        },
                        IsMailingAddressSameAsHomeAddress: !personalInfoForm.homeAddressShown,
                        MailingAddress: {
                            ...appData.Applicant.MailingAddress,
                            AddressLineOne: personalInfoForm.mailingAddress.value,
                            AddressLineTwo: personalInfoForm.mailingAptUnit.value,
                            City: personalInfoForm.mailingCity.value,
                            State: personalInfoForm.mailingState.value,
                            ZipCode: personalInfoForm.mailingZip.value,
                        },
                        HomeAddress: {
                            ...appData.Applicant.HomeAddress,
                            AddressLineOne: personalInfoForm.homeAddress.value,
                            AddressLineTwo: personalInfoForm.homeAptUnit.value,
                            City: personalInfoForm.homeCity.value,
                            State: personalInfoForm.homeState.value,
                            ZipCode: personalInfoForm.homeZip.value,
                        },
                        SocialSecurityNumber: {
                            FullSocialSecurityNumber: personalInfoForm.ssn.value,
                        },
                        DateOfBirth: dob,
                    };
                    await SessionStorage.setCipherWithCurrentScope(
                        'preApprovalFullAppData',
                        appData,
                    );
                } else {
                    appData.PersonalInformation = {
                        ...appData.PersonalInformation,
                        FirstName: acquisitionData?.firstName?.value,
                        MiddleInitial: personalInfoForm.middleInitial.value,
                        LastName: acquisitionData?.lastName?.value,
                        NameSuffix: personalInfoForm.suffix.value,
                        IsMailingHomeAddressSame: !personalInfoForm.homeAddressShown,
                        MailingAddressLine1: personalInfoForm.mailingAddress.value,
                        MailingAddressLine2: personalInfoForm.mailingAptUnit.value,
                        MailingCity: personalInfoForm.mailingCity.value,
                        MailingState: personalInfoForm.mailingState.value,
                        MailingZipCode: personalInfoForm.mailingZip.value,
                        HomeAddressLine1: personalInfoForm.homeAddress.value,
                        HomeAddressLine2: personalInfoForm.homeAptUnit.value,
                        HomeCity: personalInfoForm.homeCity.value,
                        HomeState: personalInfoForm.homeState.value,
                        HomeZipCode: personalInfoForm.homeZip.value,
                        Ssn: personalInfoForm.ssn.value,
                        DateOfBirth: dob,
                        DOBMonth: acquisitionData.dateOfBirthMonth.value,
                        DOBDay: acquisitionData.dateOfBirthDay.value,
                        DOBYear: acquisitionData.dateOfBirthYear.value,
                    };
                    await SessionStorage.setCipherWithCurrentScope(
                        'prequalFullAppData',
                        appData,
                    );
                }
                setTopInEditMode(false);
                setisPersonalInfoInEditMode(false);
                setIsInputBlurred(prev => {
                    return {
                        ...prev,
                        ssn: false,
                    };
                });
            }
        } else if (!bottomInEditMode) {
            setBottomInEditMode(true);
            setisPersonalInfoInEditMode(true);
        } else if (await validateBottomForm()) {
            if (props.partnerHostedVersion) {
                const flowAppData = SessionStorage.tryGetObject('flowApplicationData', {});
                flowAppData.parties[0].identification = {
                    type: 'SSN',
                    value: ssn,
                };

                SessionStorage.setObjectWithCurrentScope(
                    'flowApplicationData',
                    flowAppData,
                );

                setBottomInEditMode(false);
                setisPersonalInfoInEditMode(false);
                setIsInputBlurred(prev => {
                    return {
                        ...prev,
                        ssn: false,
                    };
                });

            } else {
                const appData = await getAppData();
                setIsInputBlurred(prev => {
                    return {
                        ...prev,
                        income: false,
                    };
                });
                if (getAcquisitionFlow() === AcquisitionFlow.PA) {
                    appData.Applicant = {
                        ...appData.Applicant,
                        PrimaryPhoneNumber: {
                            Number: acquisitionData.phoneNumber.value.replace(/[^\d]/g, ''),
                        },
                        EmailAddress: {
                            Value: acquisitionData.emailAddress.value,
                        },
                        MonthlyIncome: personalInfoForm.monthlyIncome.value,
                    };
                    await SessionStorage.setCipherWithCurrentScope(
                        'preApprovalFullAppData',
                        appData,
                    );
                } else {
                    appData.PersonalInformation = {
                        ...appData.PersonalInformation,
                        PrimaryPhoneNumber: acquisitionData.phoneNumber.value.replace(/[^\d]/g, ''),
                        EmailAddress: acquisitionData.emailAddress.value,
                        MonthlyIncome: personalInfoForm.monthlyIncome.value,
                    };
                    await SessionStorage.setCipherWithCurrentScope(
                        'prequalFullAppData',
                        appData,
                    );
                }
                setBottomInEditMode(false);
                personalInformationWrite({
                    phoneNumber: { state: 'neutral' },
                    emailAddress: { state: 'neutral' },
                })
            }
        }
        setTimeout(() => {
            window.dispatchEvent(new Event(APP_VERIFICATION_EVENT));
        });
    }, [topInEditMode, bottomInEditMode, personalInfoForm.mailingAddress, personalInfoForm.mailingAptUnit, personalInfoForm.mailingCity, personalInfoForm.mailingState, personalInfoForm.mailingZip, personalInfoForm.homeAddress, personalInfoForm.homeAptUnit, personalInfoForm.homeCity, personalInfoForm.homeState, personalInfoForm.homeZip, personalInfoForm.homeAddressShown, personalInfoForm.dob, personalInfoForm.email, personalInfoForm.phoneNumber, personalInfoForm.monthlyIncome, personalInfoForm.firstName, personalInfoForm.lastName, ssn, ssnValid, personalInfoForm.ssn, personalInfoForm.suffix]);

    const validateTopForm = async () => {
        if (primaryHomeABInfo) {
            return validateTopFormPrimaryHome();
        } else {
            let valid = true;
            const acquisitionData = getPersonalInformation();
            const firstNameValid = acquisitionData?.firstName?.value && regexContainer.allow.first_name.test(acquisitionData?.firstName?.value.trim());
            const lastNameValid = acquisitionData?.lastName?.value && regexContainer.allow.last_name.test(acquisitionData?.lastName?.value.trim());
            const dob = getPersonalInformationDob();

            if (!firstNameValid) {
                personalInformationWrite({
                    firstName: { state: 'error', errorType: 'requiredField' }
                });
                valid = false;
            }
            if (!lastNameValid) {
                personalInformationWrite({
                    lastName: { state: 'error', errorType: 'requiredField' }
                });
                valid = false;
            }
            if (!personalInfoForm.mailingAddress.value) {
                setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingAddress: { value: '', valid: false } }));
                valid = false;
            }
            if (!personalInfoForm.mailingCity.value) {
                setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingCity: { value: '', valid: false } }));
                valid = false;
            }
            if (!personalInfoForm.mailingState.value) {
                setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingState: { value: '', valid: false } }));
                valid = false;
            }
            if (!personalInfoForm.mailingZip.value) {
                setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingZip: { value: '', valid: false } }));
                valid = false;
            }
            if (!personalInfoForm.ssn.value) {
                setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, ssn: { value: '', valid: false } }));
                valid = false;
            } else {
                const validateSsn = validateZerosSSN(personalInfoForm.ssn.value) && personalInfoForm.ssn.value.length === 9;
                valid = validateSsn && valid;
            }
            if (!isDateValid(dob)) {
                valid = false;
            }
            if (personalInfoForm.suffix.value && !regexValidation().allow.name_suffix.exec(personalInfoForm.suffix.value)[0]) {
                valid = false;
            }
            if (acquisitionData.firstName.state === 'error' || acquisitionData.lastName.state === 'error' || !personalInfoForm.mailingAddress.valid || !personalInfoForm.mailingCity.valid || !personalInfoForm.mailingZip.valid || !personalInfoForm.ssn.valid || !personalInfoForm.suffix.valid || !personalInfoForm.mailingState.valid) {
                valid = false;
            }
            if (personalInfoForm.homeAddressShown) {
                if (!personalInfoForm.homeAddress.value) {
                    setPersonalInfoForm((prev: any) => ({ ...prev, homeAddress: { value: '', valid: false } }));
                    setPersonalInfoErrors((prev: any) => ({ ...prev, HomeAddressLine1: 'RequiredField' }));
                    valid = false;
                }
                if (!personalInfoForm.homeCity.value) {
                    setPersonalInfoForm((prev: any) => ({ ...prev, homeCity: { value: '', valid: false } }));
                    setPersonalInfoErrors((prev: any) => ({ ...prev, HomeCity: 'RequiredField' }));
                    valid = false;
                }
                if (!personalInfoForm.homeState.value) {
                    setPersonalInfoForm((prev: any) => ({ ...prev, homeState: { value: '', valid: false } }));
                    setPersonalInfoErrors((prev: any) => ({ ...prev, HomeState: 'RequiredField' }));
                    valid = false;
                }
                if (!personalInfoForm.homeZip.value) {
                    setPersonalInfoForm((prev: any) => ({ ...prev, homeZip: { value: '', valid: false } }));
                    setPersonalInfoErrors((prev: any) => ({ ...prev, HomeZipCode: 'RequiredField' }));
                    valid = false;
                }
                if (!personalInfoForm.homeAddress.valid) {
                    valid = false;
                }
                if (loqateEnabled && personalInfoForm.homeAddress.value && personalInfoForm.homeCity.value && personalInfoForm.homeState.value && personalInfoForm.homeZip.value) {
                    const addressData = await getAddressData(personalInfoForm.homeAddress.value, personalInfoForm.homeAptUnit.value, personalInfoForm.homeCity.value, personalInfoForm.homeState.value, personalInfoForm.homeZip.value);
                    if (!addressData?.valid || addressData?.commercial || homeAddressCheckRegex.test(personalInfoForm.homeAddress.value ?? '')) {

                        valid = false;
                        setPersonalInfoForm((prev: any) => ({
                            ...prev,
                            homeAddress: { value: '', valid: false },
                            homeAptUnit: { value: '', valid: true },
                            homeCity: { value: '', valid: false },
                            homeState: { value: '', valid: false },
                            homeZip: { value: '', valid: false },
                        }));
                        setPersonalInfoErrors((prev: any) => ({
                            ...prev,
                            HomeAddressLine1: 'RequiredField',
                            HomeCity: 'RequiredField',
                            HomeState: 'RequiredField',
                            HomeZipCode: 'RequiredField',
                        }));

                        //manually trigger interal input error state
                        refreshAddressState(homeAddressRef.current);
                        refreshAddressState(homeCityRef.current);
                        refreshAddressState(homeZipRef.current);
                        refreshAddressState(homeStateDropdownRef.current);

                    } else {
                        setPersonalInfoForm((prev: any) => ({
                            ...prev,
                            homeAddress: { value: prev.homeAddress.value, valid: true },
                            homeCity: { value: prev.homeCity.value, valid: true },
                            homeState: { value: prev.homeState.value, valid: true },
                            homeZip: { value: prev.homeZip.value, valid: true },
                        }));
                        setPersonalInfoErrors((prev: any) => ({
                            ...prev,
                            HomeAddressLine1: 'None',
                            HomeCity: 'None',
                            HomeState: 'None',
                            HomeZipCode: 'None',
                        }));
                    }
                }
            }
            if (loqateEnabled && personalInfoForm.mailingAddress.value && personalInfoForm.mailingCity.value && personalInfoForm.mailingState.value && personalInfoForm.mailingZip.value) {
                const addressData = await getAddressData(personalInfoForm.mailingAddress.value, personalInfoForm.mailingAptUnit.value, personalInfoForm.mailingCity.value, personalInfoForm.mailingState.value, personalInfoForm.mailingZip.value, 'mailing');
                if (!addressData?.valid) {
                    valid = false;

                    setPersonalInfoForm((personalInfoForm: any) => ({
                        ...personalInfoForm,
                        mailingAddress: { value: '', valid: false },
                        mailingAptUnit: { value: '', valid: true },
                        mailingCity: { value: '', valid: false },
                        mailingState: { value: '', valid: false },
                        mailingZip: { value: '', valid: false },
                    }));

                    if (!personalInfoForm.homeAddress.value) {
                        setPersonalInfoForm((prev: any) => ({ ...prev, homeAddress: { value: '', valid: true } }));
                        setPersonalInfoErrors((prev: any) => ({ ...prev, HomeAddressLine1: 'None' }));
                    }
                    if (!personalInfoForm.homeCity.value) {
                        setPersonalInfoForm((prev: any) => ({ ...prev, homeCity: { value: '', valid: true } }));
                        setPersonalInfoErrors((prev: any) => ({ ...prev, HomeCity: 'None' }));
                    }
                    if (!personalInfoForm.homeState.value) {
                        setPersonalInfoForm((prev: any) => ({ ...prev, homeState: { value: '', valid: true } }));
                        setPersonalInfoErrors((prev: any) => ({ ...prev, HomeState: 'None' }));
                    }
                    if (!personalInfoForm.homeZip.value) {
                        setPersonalInfoForm((prev: any) => ({ ...prev, homeZip: { value: '', valid: true } }));
                        setPersonalInfoErrors((prev: any) => ({ ...prev, HomeZipCode: 'None' }));
                    }
                } else {
                    setPersonalInfoForm((personalInfoForm: any) => ({
                        ...personalInfoForm,
                        mailingAddress: { value: personalInfoForm.mailingAddress.value, valid: true },
                        mailingAptUnit: { value: personalInfoForm.mailingAptUnit.value, valid: true },
                        mailingCity: { value: personalInfoForm.mailingCity.value, valid: true },
                        mailingState: { value: personalInfoForm.mailingState.value, valid: true },
                        mailingZip: { value: personalInfoForm.mailingZip.value, valid: true },
                    }));
                    if (addressData.commercial && !personalInfoForm.homeAddressShown) {
                        setPersonalInfoForm((prev: any) => ({ ...prev, homeAddressShown: true }));
                        if (!personalInfoForm.homeAddress.value) {
                            setPersonalInfoForm((prev: any) => ({ ...prev, homeAddress: { value: '', valid: false } }));
                            setPersonalInfoErrors((prev: any) => ({ ...prev, HomeAddressLine1: 'RequiredField' }));
                            valid = false;
                        }
                        if (!personalInfoForm.homeCity.value) {
                            setPersonalInfoForm((prev: any) => ({ ...prev, homeCity: { value: '', valid: false } }));
                            setPersonalInfoErrors((prev: any) => ({ ...prev, HomeCity: 'RequiredField' }));
                            valid = false;
                        }
                        if (!personalInfoForm.homeState.value) {
                            setPersonalInfoForm((prev: any) => ({ ...prev, homeState: { value: '', valid: false } }));
                            setPersonalInfoErrors((prev: any) => ({ ...prev, HomeState: 'RequiredField' }));
                            valid = false;
                        }
                        if (!personalInfoForm.homeZip.value) {
                            setPersonalInfoForm((prev: any) => ({ ...prev, homeZip: { value: '', valid: false } }));
                            setPersonalInfoErrors((prev: any) => ({ ...prev, HomeZipCode: 'RequiredField' }));
                            valid = false;
                        }
                        if (!personalInfoForm.homeAddress.valid) {
                            valid = false;
                        }
                    }
                }
            }
            return valid;
        }
    };


    const validateTopFormPrimaryHome = async (): Promise<boolean> => {
        let valid = true;
        let mailingValid = true;
        const acquisitionData = getPersonalInformation();
        const firstNameValid = acquisitionData?.firstName?.value && regexContainer.allow.first_name.test(acquisitionData?.firstName?.value.trim());
        const lastNameValid = acquisitionData?.lastName?.value && regexContainer.allow.last_name.test(acquisitionData?.lastName?.value.trim());
        const dob = getPersonalInformationDob();
        const isDobValid = acquisitionData.dateOfBirthMonth.errorType === 'None' && acquisitionData.dateOfBirthDay.errorType === 'None' && acquisitionData.dateOfBirthYear.errorType === 'None';

        if (!firstNameValid) {
            personalInformationWrite({
                firstName: { state: 'error', errorType: 'requiredField' }
            });
            valid = false;
        }
        if (!lastNameValid) {
            personalInformationWrite({
                lastName: { state: 'error', errorType: 'requiredField' }
            });
            valid = false;
        }
        if (!personalInfoForm.ssn.value) {
            setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, ssn: { value: '', valid: false } }));
            valid = false;
        } else {
            const validateSsn = validateZerosSSN(personalInfoForm.ssn.value) && personalInfoForm.ssn.value.length === 9;
            valid = validateSsn && valid;
        }
        if (!isDateValid(dob)) {
            valid = false;
        }
        if (personalInfoForm.suffix.value && !regexValidation().allow.name_suffix.exec(personalInfoForm.suffix.value)[0]) {
            valid = false;
        }
        if (acquisitionData.firstName.state === 'error' || acquisitionData.lastName.state === 'error' || !personalInfoForm.mailingAddress.valid || !personalInfoForm.mailingCity.valid || !personalInfoForm.mailingZip.valid || !personalInfoForm.ssn.valid || !isDobValid || !personalInfoForm.suffix.valid || !personalInfoForm.mailingState.valid) {
            valid = false;
        }
        if (!personalInfoForm.homeAddress.value) {
            setPersonalInfoForm((prev: any) => ({ ...prev, homeAddress: { value: '', valid: false } }));
            setPersonalInfoErrors((prev: any) => ({ ...prev, HomeAddressLine1: 'RequiredField' }));
            valid = false;
        }
        if (!personalInfoForm.homeCity.value) {
            setPersonalInfoForm((prev: any) => ({ ...prev, homeCity: { value: '', valid: false } }));
            setPersonalInfoErrors((prev: any) => ({ ...prev, HomeCity: 'RequiredField' }));
            valid = false;
        }
        if (!personalInfoForm.homeState.value) {
            setPersonalInfoForm((prev: any) => ({ ...prev, homeState: { value: '', valid: false } }));
            setPersonalInfoErrors((prev: any) => ({ ...prev, HomeState: 'RequiredField' }));
            valid = false;
        }
        if (!personalInfoForm.homeZip.value) {
            setPersonalInfoForm((prev: any) => ({ ...prev, homeZip: { value: '', valid: false } }));
            setPersonalInfoErrors((prev: any) => ({ ...prev, HomeZipCode: 'RequiredField' }));
            valid = false;
        }
        if (!personalInfoForm.homeAddress.valid) {
            valid = false;
        }
        if (loqateEnabled && personalInfoForm.homeAddress.value && personalInfoForm.homeCity.value && personalInfoForm.homeState.value && personalInfoForm.homeZip.value) {
            const addressData = await getAddressData(personalInfoForm.homeAddress.value, personalInfoForm.homeAptUnit.value, personalInfoForm.homeCity.value, personalInfoForm.homeState.value, personalInfoForm.homeZip.value);
            if (!addressData?.valid || addressData?.commercial || homeAddressCheckRegex.test(personalInfoForm.homeAddress.value ?? '')) {

                valid = false;
                setPersonalInfoForm((prev: any) => ({
                    ...prev,
                    homeAddress: { value: '', valid: false },
                    homeAptUnit: { value: '', valid: true },
                    homeCity: { value: '', valid: false },
                    homeState: { value: '', valid: false },
                    homeZip: { value: '', valid: false },
                }));
                setPersonalInfoErrors((prev: any) => ({
                    ...prev,
                    HomeAddressLine1: 'RequiredField',
                    HomeCity: 'RequiredField',
                    HomeState: 'RequiredField',
                    HomeZipCode: 'RequiredField',
                }));

                refreshAddressState(homeAddressRef?.current);
                refreshAddressState(homeCityRef?.current);
                refreshAddressState(homeZipRef?.current);
                refreshAddressState(homeStateDropdownRef?.current);

            } else {
                setPersonalInfoForm((prev: any) => ({
                    ...prev,
                    homeAddress: { value: prev.homeAddress.value, valid: true },
                    homeCity: { value: prev.homeCity.value, valid: true },
                    homeState: { value: prev.homeState.value, valid: true },
                    homeZip: { value: prev.homeZip.value, valid: true },
                }));
                setPersonalInfoErrors((prev: any) => ({
                    ...prev,
                    HomeAddressLine1: 'None',
                    HomeCity: 'None',
                    HomeState: 'None',
                    HomeZipCode: 'None',
                }));
            }
        }
        if (mailingCheckboxRef.current) {
            if (loqateEnabled && personalInfoForm.mailingAddress.value && personalInfoForm.mailingCity.value && personalInfoForm.mailingState.value && personalInfoForm.mailingZip.value) {
                const addressData = await getAddressData(personalInfoForm.mailingAddress.value, personalInfoForm.mailingAptUnit.value, personalInfoForm.mailingCity.value, personalInfoForm.mailingState.value, personalInfoForm.mailingZip.value, 'mailing');
                if (!addressData?.valid) {
                    mailingValid = false;

                    setPersonalInfoForm((personalInfoForm: any) => ({
                        ...personalInfoForm,
                        mailingAddress: { value: '', valid: false },
                        mailingAptUnit: { value: '', valid: true },
                        mailingCity: { value: '', valid: false },
                        mailingState: { value: '', valid: false },
                        mailingZip: { value: '', valid: false },
                    }));
                } else {
                    setPersonalInfoForm((personalInfoForm: any) => ({
                        ...personalInfoForm,
                        mailingAddress: { value: personalInfoForm.mailingAddress.value, valid: true },
                        mailingAptUnit: { value: personalInfoForm.mailingAptUnit.value, valid: true },
                        mailingCity: { value: personalInfoForm.mailingCity.value, valid: true },
                        mailingState: { value: personalInfoForm.mailingState.value, valid: true },
                        mailingZip: { value: personalInfoForm.mailingZip.value, valid: true },
                    }));
                    setPersonalInfoErrors((prev: any) => ({
                        ...prev,
                        mailingAddress: 'None',
                        mailingCity: 'None',
                        mailingState: 'None',
                        mailingZip: 'None',
                    }));
                }
            } else {
                if (!personalInfoForm.mailingAddress.value) {
                    setPersonalInfoForm((prev: any) => ({ ...prev, mailingAddress: { value: '', valid: false } }));
                    setPersonalInfoErrors((prev: any) => ({ ...prev, MailingAddressLine1: 'RequiredField' }));
                    mailingValid = false;
                }
                if (!personalInfoForm.mailingCity.value) {
                    setPersonalInfoForm((prev: any) => ({ ...prev, mailingCity: { value: '', valid: false } }));
                    setPersonalInfoErrors((prev: any) => ({ ...prev, MailingCity: 'RequiredField' }));
                    mailingValid = false;
                }
                if (!personalInfoForm.mailingState.value) {
                    setPersonalInfoForm((prev: any) => ({ ...prev, mailingState: { value: '', valid: false } }));
                    setPersonalInfoErrors((prev: any) => ({ ...prev, MailingState: 'RequiredField' }));
                    mailingValid = false;
                }
                if (!personalInfoForm.mailingZip.value) {
                    setPersonalInfoForm((prev: any) => ({ ...prev, mailingZip: { value: '', valid: false } }));
                    setPersonalInfoErrors((prev: any) => ({ ...prev, MailingZipCode: 'RequiredField' }));
                    mailingValid = false;
                }
                if (!personalInfoForm.mailingAddress.valid) {
                    mailingValid = false;
                }
            }
        }
        return valid && mailingValid;
    };

    const validateEmail = async () => {
        const acquisitionData = getPersonalInformation();
        const emailValue = acquisitionData.emailAddress.value ?? '';
        const emailDomain = emailValue.toLowerCase().substring(emailValue.indexOf('@') + 1);
        const isEmailLocallyInvalid = !isValidEmailFormat(emailValue)
            || elist.includes(emailDomain);
        const result: boolean = isEmailLocallyInvalid
            ? false
            : emailValue === lastEmail.value
                ? lastEmail.result
                : !!(await emailValidationApi(emailValue));
        setLastEmail({ value: emailValue, result });
        personalInformationWrite({
            emailAddress: { value: emailValue, state: result ? 'success' : 'error' }
        })
        return result;
    };

    const validatePhone = async () => {
        const acquisitionData = getPersonalInformation();
        const result = acquisitionData.phoneNumber.value === lastPhone.value ? lastPhone.result : !!(await phoneValidationApi(acquisitionData.phoneNumber.value));
        setLastPhone({ value: acquisitionData.phoneNumber.value, result });
        return result;
    };

    const validateBottomForm = async (): Promise<boolean> => {
        if (props.partnerHostedVersion) {
            return ssnValid;
        }

        const acquisitionData = getPersonalInformation();
        const validations = {
            income: personalInfoForm.monthlyIncome.valid && (!!personalInfoForm.monthlyIncome.value),
            phone: acquisitionData.phoneNumber.errorType === 'None' && acquisitionData.phoneNumber.state !== 'error',
            email: acquisitionData.emailAddress.errorType === 'None' && acquisitionData.emailAddress.state !== 'error',
        };

        const isAdobeMode = SessionStorage.contains('adobeMode');
        const useEmailApi = SessionStorage.contains('useEmailApi');
        const usePhoneApi = SessionStorage.contains('usePhoneApi');

        const isAuthor = ['4502', '3000'].some((entry) => window.location.port.includes(entry));

        const { allow: { phone: phoneAllowRegex } } = regexValidation();

        const emailValue = acquisitionData?.emailAddress?.value;
        if (
            !emailValue
            || !isValidEmailFormat(emailValue)
            || (elist.length > 0 && elist.includes(emailValue.toLowerCase().substring(emailValue.indexOf('@') + 1)))
        ) {
            validations.email = false;
        } else if (
            !props.lockEmail && (useEmailApi || (!isAdobeMode && !isAuthor))
        ) {
            validations.email = !!(await validateEmail());
        }
        const phoneValue = acquisitionData?.phoneNumber?.value?.replaceAll('-', '');
        let phoneErrorType: TErrorType;
        if (!phoneValue) {
            validations.phone = false;
            phoneErrorType = 'requiredField'
        } else if (            
            phoneValue.length !== 10
            || (!phoneValue.match(phoneAllowRegex))
            || (plist.length > 0 && plist.includes(phoneValue))
        ) {
            validations.phone = false;
            phoneErrorType = 'invalidInput';
        } else if (
            (usePhoneApi || (!isAdobeMode && !isAuthor))
        ) {
            validations.phone = !!(await validatePhone());
            phoneErrorType = 'thirdParty';
        }
        const emailErrorType: TErrorType = validations.email
            ? 'None'
            : !emailValue
                ? 'requiredField'
                : !isValidEmailFormat(emailValue)
                    ? 'invalidInput'
                    : 'thirdParty';
        personalInformationWrite({
            emailAddress: { state: validations.email ? 'success' : 'error', errorType: emailErrorType},
            phoneNumber: { state: validations.phone ? 'success' : 'error', errorType: validations.phone ? 'None' : phoneErrorType },
            monthlyIncome: { state: validations.income ? 'success' : 'error', errorType: validations.income ? 'None' : 'requiredField' }
        });

        if (!validations.income) {
            incomeRef?.current?.dispatchEvent(new Event('blur'));
        }

        return Object.values(validations).every((entry => entry));
    };

    const handleHomeAddressUpdate = (property: string, newVal: string) => {
        const propertyMap: Record<string, string> = {
            addressLineOne: 'homeAddress',
            addressLineTwo: 'homeAptUnit',
            city: 'homeCity',
            state: 'homeState',
            zip: 'homeZip',
        };
        const formProperty = propertyMap[property];
        if (formProperty) {
            setPersonalInfoForm((prev: any) => ({
                ...prev,
                [formProperty]: { value: newVal, valid: true },
            }));
        }
    };

    const printedName = (): string => {
        const acquisitionData = getPersonalInformation();
        let nameStr = acquisitionData?.firstName?.value ? acquisitionData.firstName.value : '';
        if (acquisitionData.middleInitial.value) {
            nameStr += ' ' + acquisitionData.middleInitial.value;
        }
        if (acquisitionData?.lastName?.value) {
            nameStr += ' ' + acquisitionData.lastName.value;
        }
        if (acquisitionData.suffix.value) {
            nameStr += ' ' + acquisitionData.suffix.value;
        }

        return nameStr;
    };

    const checkCommercial = async () => {
        if (loqateEnabled && personalInfoForm.mailingAddress.value) {
            const POBoxFound = homeAddressCheckRegex.test(personalInfoForm.mailingAddress.value);
            setPersonalInfoForm((prev: any) => ({ ...prev, homeAddressShown: POBoxFound }));

            if (loqateEnabled && personalInfoForm.mailingCity.value && personalInfoForm.mailingState.value && personalInfoForm.mailingZip.value && !document.querySelector('.pcaselected:hover')) {
                const addressData = await getAddressData(personalInfoForm.mailingAddress.value, personalInfoForm.mailingAptUnit.value, personalInfoForm.mailingCity.value, personalInfoForm.mailingState.value, personalInfoForm.mailingZip.value, 'mailing');

                if (!addressData?.valid) {
                    validateTopForm();
                }

                setPersonalInfoForm((prev: any) => ({ ...prev, homeAddressShown: addressData?.commercial && addressData?.valid }));
            } else {
                setPersonalInfoForm((prev: any) => ({ ...prev, homeAddressShown: false }));
            }
        } else if (!loqateEnabled && personalInfoForm.mailingAddress.value) {
            const POBoxFound = homeAddressCheckRegex.test(personalInfoForm.mailingAddress.value);
            setPersonalInfoForm((prev: any) => ({ ...prev, homeAddressShown: POBoxFound }));
        } else {
            setPersonalInfoForm((prev: any) => ({ ...prev, homeAddressShown: false }));
        }
    };

    const formatPhoneNumber = (phone: string) => {
        const onlyNumbers = phone?.replace(/[^0-9]/g, '').slice(0, 10);
        const formatted = onlyNumbers
            ? (onlyNumbers.length < 4
                ? `${onlyNumbers}`
                : onlyNumbers.length > 3 && onlyNumbers.length <= 6
                    ? `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(3)}`
                    : `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(
                        3,
                        6,
                    )}-${onlyNumbers.slice(6)}`
            ) : '';

        return formatted;
    };

    const checkAptUnitPoBox = (aptUnit: string, type: string) => {
        if (aptUnit && props.aptUnitPoBox) {
            const POBoxFound = homeAddressCheckRegex.test(aptUnit);
            if (type === 'mailing') {
                setPersonalInfoForm((prev: any) => ({ ...prev, homeAddressShown: POBoxFound }));
            } else if (POBoxFound && type === 'home') {
                setPersonalInfoForm((prev: any) => ({
                    ...prev,
                    homeAddress: { value: '', valid: false },
                    homeAptUnit: { value: '', valid: true },
                    homeCity: { value: '', valid: true },
                    homeState: { value: '', valid: true },
                    homeZip: { value: '', valid: true },
                }));
                setPersonalInfoErrors((prev: any) => ({
                    ...prev,
                    HomeAddressLine1: 'RequiredField',
                    HomeCity: 'None',
                    HomeState: 'None',
                    HomeZipCode: 'None',
                }));
            }
        }
    };

    const checkHome = async () => {
        if (loqateEnabled && personalInfoForm.homeAddress.value && personalInfoForm.homeCity.value && personalInfoForm.homeState.value && personalInfoForm.homeZip.value && !document.querySelector('.pcaselected:hover')) {
            const homeAddressData = await getAddressData(personalInfoForm.homeAddress.value, personalInfoForm.homeAptUnit.value, personalInfoForm.homeCity.value, personalInfoForm.homeState.value, personalInfoForm.homeZip.value, 'home');

            if (!homeAddressData?.valid || homeAddressData?.commercial) {
                validateTopForm();
            }
        }
    };

    //remove when all inputs handle this in their containers
    const handleInputBlur = (inputKey: string) => {
        setIsInputBlurred(prev => {
            return {
                ...prev,
                [inputKey]: true,
            };
        });
    };

    useImperativeHandle(ref, () => {
        return {
            inEditMode: topInEditMode || bottomInEditMode,
        };
    }, [topInEditMode, bottomInEditMode]);

    useEffect(() => {
        getAppData().then(appData => {
            if (!appData?.parties?.[0] && !appData?.PersonalInformation && !appData?.Applicant) {
                return;
            }

            if (getAcquisitionFlow() === AcquisitionFlow.PHA) {
                const homeAddress = appData.parties[0].addresses.find((addr) => addr.usageType === 'HOME');
                const mailingAddress = appData.parties[0].addresses.find((addr) => addr.usageType === 'MAILING');
                const dateParts = appData.parties[0].dateOfBirth.split(/[\/-]/);;
                const email = appData.parties[0].emails[0].value;
                const phone = appData.parties[0].phones[0].value;

                if (homeAddress) {
                    setPersonalInfoForm((prev: any) => ({
                        ...prev,
                        homeAddressShown: true,
                        homeAddress: { value: homeAddress.addressLine1, valid: true },
                        homeAptUnit: { value: homeAddress.addressLine2?.replace(/#/g, ''), valid: true },
                        homeCity: { value: homeAddress.city, valid: true },
                        homeState: { value: homeAddress.state, valid: true },
                        homeZip: { value: homeAddress.zipCode?.slice(0, 5), valid: true },
                    }));
                }

                setSsn(appData.parties[0].identification.value);
                setLastEmail({ value: email, result: true });

                personalInformationWrite({
                    firstName: { value: appData.parties[0].firstName, state: 'neutral', errorType: 'None' },
                    middleInitial: { value: appData.parties[0].middleInitial, state: 'neutral', errorType: 'None' },
                    lastName: { value: appData.parties[0].lastName, state: 'neutral', errorType: 'None' },
                    suffix: { value: appData.parties[0].suffix, state: 'neutral', errorType: 'None' },
                    mailingAddressLineOne: { value: mailingAddress.addressLine1, state: 'neutral', errorType: 'None' },
                    mailingAddressLineTwo: { value: mailingAddress.addressLine2?.replace(/#/g, ''), state: 'neutral', errorType: 'None' },
                    mailingAddressCity: { value: mailingAddress.city, state: 'neutral', errorType: 'None' },
                    mailingAddressState: { value: mailingAddress.state, state: 'neutral', errorType: 'None' },
                    mailingAddressZipCode: { value: mailingAddress.zipCode?.slice(0, 5), state: 'neutral', errorType: 'None' },
                    ssn: { value: appData.parties[0].identification.value, state: 'neutral', errorType: 'None' },
                    dateOfBirthMonth: { value: dateParts[0], state: 'neutral', errorType: 'None' },
                    dateOfBirthDay: { value: dateParts[1], state: 'neutral', errorType: 'None' },
                    dateOfBirthYear: { value: dateParts[2], state: 'neutral', errorType: 'None' },
                    emailAddress: { value: email, state: 'neutral', errorType: 'None' },
                    phoneNumber: { value: phone, state: 'neutral', errorType: 'None' },
                    monthlyIncome: {
                        value: new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        }).format(appData.parties[0].income.amount), state: 'neutral', errorType: 'None',
                    },
                });

                return;
            }

            if (getAcquisitionFlow() === AcquisitionFlow.PA) {
                const retrievedEmail = appData.Applicant.EmailAddress.Value;
                const retrievedPhone = appData.Applicant.PrimaryPhoneNumber.Number;
                const dateParts = appData.Applicant.DateOfBirth.split(/[\/-]/);

                setSsn(appData.Applicant.SocialSecurityNumber.FullSocialSecurityNumber);
                setLastEmail({ value: retrievedEmail, result: true });

                personalInformationWrite({
                    firstName: { value: appData.Applicant.ApplicantName.FirstName, state: 'neutral', errorType: 'None' },
                    middleInitial: { value: appData.Applicant.ApplicantName.MiddleInitial, state: 'neutral', errorType: 'None' },
                    lastName: { value: appData.Applicant.ApplicantName.LastName, state: 'neutral', errorType: 'None' },
                    suffix: { value: appData.Applicant.ApplicantName.NameSuffix, state: 'neutral', errorType: 'None' },
                    mailingAddressLineOne: { value: appData.Applicant.MailingAddress.AddressLineOne, state: 'neutral', errorType: 'None' },
                    mailingAddressLineTwo: { value: appData.Applicant.MailingAddress.AddressLineTwo, state: 'neutral', errorType: 'None' },
                    mailingAddressCity: { value: appData.Applicant.MailingAddress.City, state: 'neutral', errorType: 'None' },
                    mailingAddressState: { value: appData.Applicant.MailingAddress.State, state: 'neutral', errorType: 'None' },
                    mailingAddressZipCode: { value: appData.Applicant.MailingAddress.ZipCode, state: 'neutral', errorType: 'None' },
                    homeAddressLineOne: { value: appData.Applicant.HomeAddress.AddressLineOne, state: 'neutral', errorType: 'None' },
                    homeAddressLineTwo: { value: appData.Applicant.HomeAddress.AddressLineTwo, state: 'neutral', errorType: 'None' },
                    homeAddressCity: { value: appData.Applicant.HomeAddress.City, state: 'neutral', errorType: 'None' },
                    homeAddressState: { value: appData.Applicant.HomeAddress.State, state: 'neutral', errorType: 'None' },
                    homeAddressZipCode: { value: appData.Applicant.HomeAddress.ZipCode, state: 'neutral', errorType: 'None' },
                    ssn: { value: appData.Applicant.SocialSecurityNumber.FullSocialSecurityNumber, state: 'neutral', errorType: 'None' },
                    dateOfBirthMonth: { value: dateParts[0], state: 'neutral', errorType: 'None' },
                    dateOfBirthDay: { value: dateParts[1], state: 'neutral', errorType: 'None' },
                    dateOfBirthYear: { value: dateParts[2], state: 'neutral', errorType: 'None' },
                    emailAddress: { value: retrievedEmail, state: 'neutral', errorType: 'None' },
                    phoneNumber: { value: retrievedPhone, state: 'neutral', errorType: 'None' },
                    monthlyIncome: { value: appData.Applicant.MonthlyIncome, state: 'neutral', errorType: 'None' },
                });
                setIsHomeAddressShown(!appData.Applicant.IsMailingAddressSameAsHomeAddress);
            } else {
                const retrievedEmail = appData.PersonalInformation.EmailAddress;
                const retrievedPhone = appData.PersonalInformation.PrimaryPhoneNumber;

                setSsn(appData.PersonalInformation.Ssn);
                setLastEmail({ value: retrievedEmail, result: true });

                personalInformationWrite({
                    firstName: { value: appData.PersonalInformation.FirstName, state: 'neutral', errorType: 'None' },
                    middleInitial: { value: appData.PersonalInformation.MiddleInitial, state: 'neutral', errorType: 'None' },
                    lastName: { value: appData.PersonalInformation.LastName, state: 'neutral', errorType: 'None' },
                    suffix: { value: appData.PersonalInformation.NameSuffix, state: 'neutral', errorType: 'None' },
                    mailingAddressLineOne: { value: appData.PersonalInformation.MailingAddressLine1, state: 'neutral', errorType: 'None' },
                    mailingAddressLineTwo: { value: appData.PersonalInformation.MailingAddressLine2, state: 'neutral', errorType: 'None' },
                    mailingAddressCity: { value: appData.PersonalInformation.MailingCity, state: 'neutral', errorType: 'None' },
                    mailingAddressState: { value: appData.PersonalInformation.MailingState, state: 'neutral', errorType: 'None' },
                    mailingAddressZipCode: { value: appData.PersonalInformation.MailingZipCode, state: 'neutral', errorType: 'None' },
                    homeAddressLineOne: { value: appData.PersonalInformation.HomeAddressLine1, state: 'neutral', errorType: 'None' },
                    homeAddressLineTwo: { value: appData.PersonalInformation.HomeAddressLine2, state: 'neutral', errorType: 'None' },
                    homeAddressCity: { value: appData.PersonalInformation.HomeCity, state: 'neutral', errorType: 'None' },
                    homeAddressState: { value: appData.PersonalInformation.HomeState, state: 'neutral', errorType: 'None' },
                    homeAddressZipCode: { value: appData.PersonalInformation.HomeZipCode, state: 'neutral', errorType: 'None' },
                    ssn: { value: appData.PersonalInformation.Ssn, state: 'neutral', errorType: 'None' },
                    dateOfBirthMonth: { value: appData.PersonalInformation.DOBMonth, state: 'neutral', errorType: 'None' },
                    dateOfBirthDay: { value: appData.PersonalInformation.DOBDay, state: 'neutral', errorType: 'None' },
                    dateOfBirthYear: { value: appData.PersonalInformation.DOBYear, state: 'neutral', errorType: 'None' },
                    emailAddress: { value: retrievedEmail, state: 'neutral', errorType: 'None' },
                    phoneNumber: { value: retrievedPhone, state: 'neutral', errorType: 'None' },
                    monthlyIncome: { value: appData.PersonalInformation.MonthlyIncome, state: 'neutral', errorType: 'None' },
                });
                setIsHomeAddressShown(!appData.PersonalInformation.IsMailingHomeAddressSame);
            }
        });
    }, []);

    //remove when ssnfield container replaces inputSocialSecurity input for pha
    const handleBlurSsn = () => {
        const ssnValidCheck = personalInfoForm.ssn.value.length === 9 && validateZerosSSN(personalInfoForm.ssn.value);
        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, ssn: { value: personalInfoForm.ssn.value, valid: ssnValidCheck } }));

        handleInputBlur('ssn');
        if (!validateZerosSSN(personalInfoForm.ssn.value)) {
            setSsn('');
            setSsnValid(false);
            setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, ssn: { value: '', valid: false } }));
        }
    };

    const validateZerosSSN = (ssn: String) => {

        const firstThree = ssn.slice(0, 3);
        const middleTwo = ssn.slice(3, 5);
        const lastFour = ssn.slice(5, 10);

        return !(firstThree === '000' || middleTwo === '00' || lastFour === '0000');
    };

    const isSamePersonAuthorizedUserSetter = () => {
        const acquisitionData = getPersonalInformation();
        const piDob = getPersonalInformationDob();

        if (acquisitionData?.firstName?.value && acquisitionData?.lastName?.value && piDob) {
            const authorizedUserDOB = getAuthorizedUserDob();
            const authorizedUserString = [acquisitionData?.authorizedUserFirstName.value, acquisitionData?.authorizedUserLastName.value, formatDateOfBirth(authorizedUserDOB)].join('');
            const personalInfoString = [acquisitionData?.firstName?.value, acquisitionData?.lastName?.value, formatDateOfBirth(piDob)].join('');
            setIsSamePersonError(areStringsEqual(authorizedUserString, personalInfoString));
            setIsAuthorisedUserSectionValid(areStringsEqual(authorizedUserString, personalInfoString));
        }
    };

    const getFormattedDob = (): string => {
        const acquisitionData = getPersonalInformation();
        if (acquisitionData.dateOfBirthMonth.value && acquisitionData.dateOfBirthDay.value && acquisitionData.dateOfBirthYear.value) {
            return getFormattedDate({
                month: { value: acquisitionData.dateOfBirthMonth.value },
                day: { value: acquisitionData.dateOfBirthDay.value },
                year: { value: acquisitionData.dateOfBirthYear.value }
            }).replaceAll('/', '-');
        }
        return '';
    }

    const PartnerHostedTop = () => {
        return (
            <>
                <div className='PersonalInformation__infoContainer'>
                    <p>
                        <b>{props.nameLabel}:</b>
                    </p>
                    <p>
                        {printedName()}
                    </p>
                </div>
                <div className='PersonalInformation__infoContainer PersonalInformation__infoContainer--address'>
                    <p>
                        <b>{props.mailingAddressLabel}:</b>
                    </p>
                    <p>
                        {`${personalInfoForm.mailingAddress.value || ''} ${personalInfoForm.mailingAptUnit.value || ''}`}
                    </p>
                    <p>
                        {`${personalInfoForm.mailingCity.value || ''}, ${personalInfoForm.mailingState.value || ''} ${personalInfoForm.mailingZip.value || ''}`}
                    </p>
                </div>
                {personalInfoForm.homeAddressShown && (
                    <div className='PersonalInformation__infoContainer PersonalInformation__infoContainer--address'>
                        <p>
                            <b>{props.homeAddressLabel}:</b>
                        </p>
                        <p>
                            {`${personalInfoForm.homeAddress.value || ''} ${personalInfoForm.homeAptUnit.value || ''}`}
                        </p>
                        <p>
                            {`${personalInfoForm.homeCity.value || ''}, ${personalInfoForm.homeState.value || ''} ${personalInfoForm.homeZip.value || ''}`}
                        </p>
                    </div>
                )}
                <div className='PersonalInformation__infoContainer'>
                    <p>
                        <b>{props.dobLabel}:</b>
                    </p>
                    <p>
                        {getFormattedDob()}
                    </p>
                </div>
                <div className='PersonalInformation__infoContainer'>
                    <p>
                        <b>{props.emailLabel}:</b>
                    </p>
                    <p>
                        {getPersonalInformation().emailAddress.value || ''}
                    </p>
                </div>
                <div className='PersonalInformation__infoContainer'>
                    <p>
                        <b>{props.phoneLabel}:</b>
                    </p>
                    <p>
                        {getPersonalInformation().phoneNumber.value}
                    </p>
                </div>
                <div className='PersonalInformation__infoContainer'>
                    <p>
                        <b>{props.incomeLabel}:</b>
                    </p>
                    <p>
                        {personalInfoForm.monthlyIncome.value}
                    </p>
                </div>
            </>
        );
    };

    const handleHomeState = (newVal: string) => {
        if (!personalInfoForm.homeState.valid) {
            setPersonalInfoForm((prev: any) => ({ ...prev, homeState: { value: newVal, valid: true } }));
            setPersonalInfoErrors((prev: any) => ({ ...prev, HomeState: 'None' }));
        } else {
            setPersonalInfoForm((prev: any) => ({ ...prev, homeState: { value: newVal, valid: true } }));
        }
    };

    return (
        <section className={`ApplicationVerification__formSection ${props.isZoomedIn ? 'ApplicationVerification__formSection--zoomed' : ''} PersonalInformation`}>
            <h3 className='ApplicationVerification__sectionHeadline ApplicationVerification__sectionHeadline--personalInformation'>{props.sectionTitle}</h3>
            <Row className='PersonalInformation__topSectionWrapper mt-4'>
                <Col xs={4} md={10} className='!pl-8'>
                    <p className='PersonalInformation__requiredFields'>
                        {topInEditMode ? (
                            <React.Fragment>
                                Fields with an asterisk<sup className='ApplicationVerification__asterisk'>*</sup> are required
                            </React.Fragment>
                        ) : (
                            <React.Fragment>&nbsp;</React.Fragment>
                        )}
                    </p>
                </Col>
                <Col xs={2}
                    className='ApplicationVerification__toggleContainer ApplicationVerification__toggleContainer--personalInformation'>
                    {props.lockTopSection || props.partnerHostedVersion ? (
                        <svg className='ApplicationVerification__lock' aria-hidden='true'
                            role='img' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'>
                            <path fill='currentColor'
                                d='M400 224h-24v-72C376 68.2 307.8 0 224 0S72 68.2 72 152v72H48c-26.5 0-48 21.5-48 48v192c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V272c0-26.5-21.5-48-48-48zm-104 0H152v-72c0-39.7 32.3-72 72-72s72 32.3 72 72v72z'></path>
                        </svg>
                    ) : (
                        <a className='ApplicationVerification__editToggle topSection' id='topSectionEdit'
                            onClick={handleClickEdit}>{topInEditMode ? 'SAVE' : 'EDIT'}</a>
                    )}
                </Col>
            </Row>
            {topInEditMode && !props.partnerHostedVersion ? (
                <div className='PersonalInformation__topSectionForm'>
                    <div className='PersonalInformation__inputContainer'>
                        {getFeatureFlags('DPM-13102') ? (
                            <NameFieldWithProvider
                                fieldKey='firstName'
                                id='PersonalInformation__first__name'
                                name='First Name'
                                label='First Name'
                                onBlurCallback={() => {
                                    isSamePersonAuthorizedUserSetter();
                                }}
                            />
                        ) : (
                            <NameField
                                id='PersonalInformation__first__name'
                                inputClass='PersonalInformation__input'
                                name='First Name'
                                value={personalInfoForm.firstName.value}
                                setValueCallback={(value: string) => {
                                    setPersonalInfoForm((personalInfoForm: IPersonalInfo): IPersonalInfo => ({ ...personalInfoForm, firstName: { value: value, valid: true } }));
                                }}
                                label='First Name'
                                error={!personalInfoForm.firstName.valid}
                                onBlurCallback={(value: string, isValid: boolean) => {
                                    setPersonalInfoForm((personalInfoForm: IPersonalInfo): IPersonalInfo => ({ ...personalInfoForm, firstName: { value: value, valid: isValid } }));
                                    isSamePersonAuthorizedUserSetter();
                                }}
                                useSuccessIcon
                            />
                        )}
                    </div>
                    <div className='PersonalInformation__inputContainer'>
                        {getFeatureFlags('DPM-13102') ? (
                            <MiddleInitialFieldWithProvider
                                fieldKey='authorizedUserMiddleInitial'
                                id='PersonalInformation__middle__initial'
                                name='Middle Initial'
                                label='Middle Initial'
                            />
                        ) : (
                            <MiddleInitialField
                                id='PersonalInformation__middle__initial'
                                inputClass='PersonalInformation__input'
                                name='Middle Initial'
                                value={personalInfoForm.middleInitial.value}
                                setValueCallback={(value: string) => {
                                    setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, middleInitial: { value: value, valid: true } }));
                                }}
                                label='Middle Initial'
                                useSuccessIcon
                            />
                        )}
                    </div>
                    <div className='PersonalInformation__inputContainer'>
                        {getFeatureFlags('DPM-13102') ? (
                            <NameFieldWithProvider
                                fieldKey='lastName'
                                id='PersonalInformation__last__name'
                                name='Last Name'
                                label='Last Name'
                                onBlurCallback={() => {
                                    isSamePersonAuthorizedUserSetter();
                                }}
                            />
                        ) : (
                            <NameField
                                id='PersonalInformation__last__name'
                                inputClass='PersonalInformation__input'
                                name='Last Name'
                                value={personalInfoForm.lastName.value}
                                setValueCallback={(value: string) => {
                                    setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, lastName: { value: value, valid: true } }));
                                }}
                                label='Last Name'
                                error={!personalInfoForm.lastName.valid}
                                onBlurCallback={(value: string, isValid: boolean) => {
                                    setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, lastName: { value: value, valid: isValid } }));
                                    isSamePersonAuthorizedUserSetter();
                                }}
                                useSuccessIcon
                            />
                        )}
                    </div>
                    {getAcquisitionFlow() === AcquisitionFlow.PA &&
                        <div className='PersonalInformation__inputContainer'>
                            {getFeatureFlags('DPM-13102') ? (
                                <SuffixFieldWithProvider
                                    fieldKey='suffix'
                                    label='Suffix'
                                    name='Suffix'
                                    id='PersonalInformation__suffix'
                                />
                            ) : (
                                <SuffixField
                                    id='PersonalInformation__suffix'
                                    inputClass='PersonalInformation__input'
                                    value={personalInfoForm.suffix.value}
                                    error={!personalInfoForm.suffix.valid}
                                    setValueCallback={(value: string) => {
                                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, suffix: { value: value.replace(/[^a-zA-Z2-9]/, '').toUpperCase(), valid: true } }));
                                    }}
                                    label='Suffix'
                                    useSuccessIcon
                                    invalidErrorMessage={generateFieldErrorMessage('Suffix')}
                                />
                            )}

                        </div>
                    }
                    {primaryHomeABInfo ?
                        <React.Fragment>
                            <AddressField
                                addressType='home'
                                ref={homeAddressRef}
                                className='PersonalInformation__inputContainer block'
                                id='PersonalInformation__homeAddressLine1'
                                inputClass='PersonalInformation__input'
                                value={personalInfoForm.homeAddress.value}
                                setValueCallback={(value: string) => {
                                    const containsInvalidCharacters = regexContainer.disallow.invalid_address_characters.test(value);

                                    if (containsInvalidCharacters) {
                                        value = value.replace(regexContainer.disallow.invalid_address_characters, '');
                                    }

                                    const matchesAddress = personalInfoForm.homeAptUnit.value.toLowerCase() === value.toLowerCase();
                                    const addressFieldsAreBlank = personalInfoForm.homeAptUnit.value === '' && value === '';

                                    if (matchesAddress && !addressFieldsAreBlank) {
                                        setPersonalInfoForm((personalInfoForm: any) => ({
                                            ...personalInfoForm,
                                            homeAptUnit: { value: '', valid: true },
                                            homeAddress: { value: '', valid: false },
                                            homeCity: { value: '', valid: false },
                                            homeState: { value: '', valid: false },
                                            homeZip: { value: '', valid: false },
                                        }));
                                    } else {
                                        handleHomeAddressUpdate('addressLineOne', value);
                                    }
                                }}
                                label='Home Address'
                                valid={[personalInfoForm.homeAddress.valid, (val: boolean) => {
                                    setPersonalInfoForm((prev: any) => ({ ...prev, homeAddress: { value: prev.homeAddress.value, valid: val } }));
                                    setPersonalInfoErrors((prev: any) => ({ ...prev, HomeAddressLine1: val ? 'None' : 'RequiredField' }));
                                }]}
                                error={!personalInfoForm.homeAddress.valid}
                                requiredMessage={generateFieldErrorMessage('Home Address', 'missingValue')}
                                errorText={generateFieldErrorMessage('Home Address', 'missingValue')}
                                invalidErrorMessage={generateFieldErrorMessage('Home Address', 'thirdParty', 'address')}
                                required={true}
                                onBlurCallback={() => {
                                    checkHome();
                                }}
                                useOutsideValidation={personalInfoForm.homeAddress.valid}
                                alternate
                                useSuccessIcon
                            />
                            <ApartmentField
                                className='PersonalInformation__inputContainer block'
                                id='PersonalInformation__homeAddressLine2'
                                inputClass='PersonalInformation__input'
                                value={personalInfoForm.homeAptUnit.value}
                                setValueCallback={(value: string) => {
                                    const containsInvalidCharacters = regexContainer.disallow.invalid_address_characters.test(value);

                                    if (containsInvalidCharacters) {
                                        value = value.replace(regexContainer.disallow.invalid_address_characters, '');
                                    }

                                    const matchesAddress = personalInfoForm.homeAddress.value.toLowerCase() === value.toLowerCase();
                                    const addressFieldsAreBlank = personalInfoForm.homeAddress.value === '' && value === '';

                                    if (matchesAddress && !addressFieldsAreBlank) {
                                        setPersonalInfoForm((personalInfoForm: any) => ({
                                            ...personalInfoForm,
                                            homeAptUnit: { value: '', valid: true },
                                            homeAddress: { value: '', valid: false },
                                            homeCity: { value: '', valid: false },
                                            homeState: { value: '', valid: false },
                                            homeZip: { value: '', valid: false },
                                        }));
                                    } else {
                                        handleHomeAddressUpdate('addressLineTwo', value);
                                    }
                                }}
                                label='Home Apt/Unit #'
                                onBlurCallback={() => {
                                    checkHome();
                                    checkAptUnitPoBox(personalInfoForm.homeAptUnit.value ?? '', 'home');
                                }}
                                useOutsideValidation={personalInfoForm.homeAptUnit.value !== '' ? true : false}
                                alternate
                                addressType='home'
                                useSuccessIcon
                            />
                            <CityField
                                ref={homeCityRef}
                                className='PersonalInformation__inputContainer block'
                                id='PersonalInformation__homeAddressCity'
                                inputClass='PersonalInformation__input'
                                value={personalInfoForm.homeCity.value}
                                setValueCallback={(newVal: string) => handleHomeAddressUpdate('city', newVal)}
                                label='Home City'
                                valid={[personalInfoForm.homeCity.valid, (val: boolean) => {
                                    setPersonalInfoForm((prev: any) => ({ ...prev, homeCity: { value: prev.homeCity.value, valid: val } }));
                                    setPersonalInfoErrors((prev: any) => ({ ...prev, HomeCity: val ? 'None' : 'RequiredField' }));
                                }]}
                                error={!personalInfoForm.homeCity.valid}
                                requiredMessage={generateFieldErrorMessage('Home Address City', 'missingValue')}
                                errorText={generateFieldErrorMessage('Home Address City', 'missingValue')}
                                invalidErrorMessage={generateFieldErrorMessage('Home Address City')}
                                required={true}
                                onBlurCallback={() => {
                                    checkHome();
                                }}
                                useOutsideValidation={personalInfoForm.homeCity.valid}
                                alternate
                                useSuccessIcon
                            />
                            <div className='PersonalInformation__inputContainer PersonalInformation__dropdDown'>
                                <StateField
                                    ref={homeStateDropdownRef}
                                    id='PersonalInformation__homeAddressState'
                                    name='state'
                                    inputClass='PersonalInformation__input'
                                    value={personalInfoForm.homeState.value}
                                    setValueCallback={(newVal: string) => {
                                        handleHomeState(newVal);
                                    }}
                                    label='Home State'
                                    abbreviation={false}
                                    required
                                    valid={[personalInfoForm.homeState.valid, (val: boolean) => {
                                        setPersonalInfoForm((prev: any) => ({ ...prev, homeState: { value: prev.homeState.value, valid: val } }));
                                        setPersonalInfoErrors((prev: any) => ({ ...prev, HomeState: val ? 'None' : 'RequiredField' }));
                                    }]}
                                    error={!personalInfoForm.homeState.valid}
                                    errorText={generateFieldErrorMessage('Home Address State')}
                                    invalidErrorMessage={generateFieldErrorMessage('Home Address State', 'missingValue')}
                                    onBlurCallback={() => {
                                        checkHome();
                                    }}
                                    useOutsideValidation={personalInfoForm.homeState.valid}
                                    alternate
                                    mixed
                                    useSuccessIcon
                                    isPrefill
                                />
                            </div>
                            <ZipCodeField
                                ref={homeZipRef}
                                className='PersonalInformation__inputContainer block'
                                id='PersonalInformation__homeAddressZip'
                                inputClass='PersonalInformation__input'
                                value={personalInfoForm.homeZip.value}
                                setValueCallback={(newVal: string) => handleHomeAddressUpdate('zip', newVal)}
                                label='Home ZIP Code'
                                valid={[personalInfoForm.homeZip.valid, (val: boolean) => {
                                    setPersonalInfoForm((prev: any) => ({ ...prev, homeZip: { value: prev.homeZip.value, valid: val } }));
                                    setPersonalInfoErrors((prev: any) => ({ ...prev, HomeZipCode: val ? 'None' : 'RequiredField' }));
                                }]}
                                error={!personalInfoForm.homeZip.valid}
                                requiredMessage={generateFieldErrorMessage('Home Address ZIP Code', 'missingValue')}
                                errorText={generateFieldErrorMessage('Home Address ZIP Code')}
                                invalidErrorMessage={generateFieldErrorMessage('Home Address ZIP Code')}
                                required={true}
                                onBlurCallback={() => {
                                    if (personalInfoForm.homeZip.value.length === 5) {
                                        checkHome();
                                    }
                                }}
                                useOutsideValidation={personalInfoForm.homeZip.valid}
                                alternate
                                useSuccessIcon
                            />
                            <InputCheckbox className='mb-6' label={primaryHomeABInfo?.mailingAddressCheckboxText && ReactHtmlParser(primaryHomeABInfo?.mailingAddressCheckboxText)}
                                value={mailingCheckboxRef.current}
                                setValue={() => {
                                    mailingCheckboxRef.current = !mailingCheckboxRef.current;
                                    setMailingCheckboxSelected(!mailingCheckboxSelected);
                                    if (!mailingCheckboxRef.current) {
                                        setPersonalInfoForm((prev: any) => ({
                                            ...prev,
                                            mailingAddress: { value: '', valid: true },
                                            mailingAptUnit: { value: '', valid: true },
                                            mailingCity: { value: '', valid: true },
                                            mailingState: { value: '', valid: true },
                                            mailingZip: { value: '', valid: true },
                                        }));
                                    }
                                }} />
                            {mailingCheckboxSelected && (
                                <React.Fragment>
                                    <AddressField
                                        className='PersonalInformation__inputContainer block'
                                        addressType='mailing'
                                        ref={addressRef}
                                        id='PersonalInformation__addressLine1'
                                        maxLength={100}
                                        inputClass='PersonalInformation__input'
                                        onBlurCallback={() => {
                                            checkCommercial();
                                            checkAptUnitPoBox(personalInfoForm.mailingAptUnit.value ?? '', 'mailing');
                                        }}
                                        value={personalInfoForm.mailingAddress.value}
                                        setValueCallback={(value: string) => {
                                            const containsInvalidCharacters = regexContainer.disallow.invalid_address_characters.test(value);

                                            if (containsInvalidCharacters) {
                                                value = value.replace(regexContainer.disallow.invalid_address_characters, '');
                                            }

                                            const matchesAddress = personalInfoForm.mailingAptUnit.value.toLowerCase() === value.toLowerCase();
                                            const addressFieldsAreBlank = personalInfoForm.mailingAptUnit.value === '' && value === '';

                                            if (matchesAddress && !addressFieldsAreBlank) {
                                                setPersonalInfoForm((personalInfoForm: any) => ({
                                                    ...personalInfoForm,
                                                    mailingAptUnit: { value: '', valid: true },
                                                    mailingAddress: { value: '', valid: false },
                                                    mailingCity: { value: '', valid: false },
                                                    mailingState: { value: '', valid: false },
                                                    mailingZip: { value: '', valid: false },
                                                }));
                                            } else {
                                                setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingAddress: { value: value, valid: true } }));
                                            }
                                        }}
                                        label='Mailing Address'
                                        error={!personalInfoForm.mailingAddress.valid}
                                        requiredMessage={generateFieldErrorMessage('Mailing Street Address', 'missingValue')}
                                        errorText={generateFieldErrorMessage('Mailing Address', 'missingValue')}
                                        invalidErrorMessage={generateFieldErrorMessage('Mailing Address', 'thirdParty', 'address')}
                                        required={true}
                                        alternate
                                        useSuccessIcon
                                    />
                                    <ApartmentField
                                        className='PersonalInformation__inputContainer block'
                                        id='PersonalInformation__addressLine2'
                                        maxLength={100}
                                        inputClass='PersonalInformation__input'
                                        ref={aptRef}
                                        onBlurCallback={() => {
                                            checkCommercial();
                                            checkAptUnitPoBox(personalInfoForm.mailingAptUnit.value ?? '', 'mailing');
                                        }}
                                        value={personalInfoForm.mailingAptUnit.value}
                                        setValueCallback={(value: string) => {
                                            const containsInvalidCharacters = regexContainer.disallow.invalid_address_characters.test(value);

                                            if (containsInvalidCharacters) {
                                                value = value.replace(regexContainer.disallow.invalid_address_characters, '');
                                            }

                                            const matchesAddress = personalInfoForm.mailingAddress.value.toLowerCase() === value.toLowerCase();
                                            const addressFieldsAreBlank = personalInfoForm.mailingAddress.value === '' && value === '';

                                            if (matchesAddress && !addressFieldsAreBlank) {
                                                setPersonalInfoForm((personalInfoForm: any) => ({
                                                    ...personalInfoForm,
                                                    mailingAptUnit: { value: '', valid: true },
                                                    mailingAddress: { value: '', valid: false },
                                                    mailingCity: { value: '', valid: false },
                                                    mailingState: { value: '', valid: false },
                                                    mailingZip: { value: '', valid: false },
                                                }));
                                            } else {
                                                setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingAptUnit: { value: value, valid: true } }));
                                            }
                                        }}
                                        label='Mailing Apt/Unit #'
                                        addressType='mailing'
                                        alternate
                                        useSuccessIcon
                                    />
                                    <CityField
                                        ref={cityRef}
                                        className='PersonalInformation__inputContainer block'
                                        id='PersonalInformation__addressCity'
                                        inputClass='PersonalInformation__input'
                                        onBlurCallback={() => {
                                            checkCommercial();
                                            checkAptUnitPoBox(personalInfoForm.mailingAptUnit.value ?? '', 'mailing');
                                        }}
                                        value={personalInfoForm.mailingCity.value}
                                        setValueCallback={(value: string) => {
                                            setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingCity: { value: value, valid: true } }));
                                        }}
                                        label='Mailing City'
                                        error={!personalInfoForm.mailingCity.valid}
                                        requiredMessage={generateFieldErrorMessage('City', 'missingValue')}
                                        errorText={generateFieldErrorMessage('City', 'missingValue')}
                                        invalidErrorMessage={generateFieldErrorMessage('City')}
                                        required={true}
                                        alternate
                                        useSuccessIcon
                                    />
                                    <div className='PersonalInformation__inputContainer PersonalInformation__dropdDown' >
                                        <StateField
                                            ref={stateDropdownRef}
                                            id='PersonalInformation__addressState'
                                            name='state'
                                            mixed
                                            inputClass='PersonalInformation__input'
                                            onBlurCallback={() => {
                                                checkCommercial();
                                                checkAptUnitPoBox(personalInfoForm.mailingAptUnit.value ?? '', 'mailing');
                                            }}
                                            value={personalInfoForm.mailingState.value}
                                            setValueCallback={(value: string) => {
                                                setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingState: { value: value, valid: true } }));
                                            }}
                                            label='Mailing State'
                                            error={!personalInfoForm.mailingState.valid}
                                            errorText={generateFieldErrorMessage('State')}
                                            invalidErrorMessage={generateFieldErrorMessage('State', 'missingValue')}
                                            abbreviation={false}
                                            required
                                            alternate
                                            useSuccessIcon
                                        />
                                    </div>
                                    <ZipCodeField
                                        ref={zipRef}
                                        className='PersonalInformation__inputContainer block'
                                        id='PersonalInformation__addressZip'
                                        inputClass='PersonalInformation__input'
                                        onBlurCallback={() => {
                                            if (personalInfoForm.mailingZip.value.length === 5) {
                                                checkCommercial();
                                                checkAptUnitPoBox(personalInfoForm.mailingAptUnit.value ?? '', 'mailing');
                                            }
                                        }}
                                        value={personalInfoForm.mailingZip.value}
                                        setValueCallback={(value: string) => {
                                            setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingZip: { value: value, valid: true } }));
                                        }}
                                        label='Mailing ZIP Code'
                                        error={!personalInfoForm.mailingZip.valid}
                                        requiredMessage={generateFieldErrorMessage('ZIP Code', 'missingValue')}
                                        errorText={generateFieldErrorMessage('ZIP Code')}
                                        invalidErrorMessage={generateFieldErrorMessage('ZIP Code')}
                                        required={true}
                                        alternate
                                        useSuccessIcon
                                    />
                                </React.Fragment>
                            )}
                        </React.Fragment>
                        :
                        <>
                            <AddressField
                                className='PersonalInformation__inputContainer block'
                                addressType='mailing'
                                ref={addressRef}
                                id='PersonalInformation__addressLine1'
                                maxLength={100}
                                inputClass='PersonalInformation__input'
                                onBlurCallback={() => {
                                    checkCommercial();
                                    checkAptUnitPoBox(personalInfoForm.mailingAptUnit.value ?? '', 'mailing');
                                }}
                                value={personalInfoForm.mailingAddress.value}
                                setValueCallback={(value: string) => {
                                    const containsInvalidCharacters = regexContainer.disallow.invalid_address_characters.test(value);

                                    if (containsInvalidCharacters) {
                                        value = value.replace(regexContainer.disallow.invalid_address_characters, '');
                                    }

                                    const matchesAddress = personalInfoForm.mailingAptUnit.value.toLowerCase() === value.toLowerCase();
                                    const addressFieldsAreBlank = personalInfoForm.mailingAptUnit.value === '' && value === '';

                                    if (matchesAddress && !addressFieldsAreBlank) {
                                        setPersonalInfoForm((personalInfoForm: any) => ({
                                            ...personalInfoForm,
                                            mailingAptUnit: { value: '', valid: true },
                                            mailingAddress: { value: '', valid: false },
                                            mailingCity: { value: '', valid: false },
                                            mailingState: { value: '', valid: false },
                                            mailingZip: { value: '', valid: false },
                                        }));
                                    } else {
                                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingAddress: { value: value, valid: true } }));
                                    }
                                }}
                                label='Address'
                                error={!personalInfoForm.mailingAddress.valid}
                                requiredMessage={generateFieldErrorMessage('Mailing Street Address', 'missingValue')}
                                errorText={generateFieldErrorMessage('Mailing Address', 'missingValue')}
                                invalidErrorMessage={generateFieldErrorMessage('Mailing Address', 'thirdParty', 'address')}
                                required={true}
                                alternate
                                useSuccessIcon
                            />
                            <ApartmentField
                                className='PersonalInformation__inputContainer block'
                                id='PersonalInformation__addressLine2'
                                maxLength={100}
                                inputClass='PersonalInformation__input'
                                ref={aptRef}
                                onBlurCallback={() => {
                                    checkCommercial();
                                    checkAptUnitPoBox(personalInfoForm.mailingAptUnit.value ?? '', 'mailing');
                                }}
                                value={personalInfoForm.mailingAptUnit.value}
                                setValueCallback={(value: string) => {
                                    const containsInvalidCharacters = regexContainer.disallow.invalid_address_characters.test(value);

                                    if (containsInvalidCharacters) {
                                        value = value.replace(regexContainer.disallow.invalid_address_characters, '');
                                    }

                                    const matchesAddress = personalInfoForm.mailingAddress.value.toLowerCase() === value.toLowerCase();
                                    const addressFieldsAreBlank = personalInfoForm.mailingAddress.value === '' && value === '';

                                    if (matchesAddress && !addressFieldsAreBlank) {
                                        setPersonalInfoForm((personalInfoForm: any) => ({
                                            ...personalInfoForm,
                                            mailingAptUnit: { value: '', valid: true },
                                            mailingAddress: { value: '', valid: false },
                                            mailingCity: { value: '', valid: false },
                                            mailingState: { value: '', valid: false },
                                            mailingZip: { value: '', valid: false },
                                        }));
                                    } else {
                                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingAptUnit: { value: value, valid: true } }));
                                    }
                                }}
                                label='Apt/Unit #'
                                addressType='mailing'
                                alternate
                                useSuccessIcon
                            />
                            <CityField
                                ref={cityRef}
                                className='PersonalInformation__inputContainer block'
                                id='PersonalInformation__addressCity'
                                inputClass='PersonalInformation__input'
                                onBlurCallback={() => {
                                    checkCommercial();
                                    checkAptUnitPoBox(personalInfoForm.mailingAptUnit.value ?? '', 'mailing');
                                }}
                                value={personalInfoForm.mailingCity.value}
                                setValueCallback={(value: string) => {
                                    setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingCity: { value: value, valid: true } }));
                                }}
                                label='City'
                                error={!personalInfoForm.mailingCity.valid}
                                requiredMessage={generateFieldErrorMessage('City', 'missingValue')}
                                errorText={generateFieldErrorMessage('City', 'missingValue')}
                                invalidErrorMessage={generateFieldErrorMessage('City')}
                                required={true}
                                alternate
                                useSuccessIcon
                            />
                            <div className='PersonalInformation__inputContainer PersonalInformation__dropdDown' >
                                <StateField
                                    ref={stateDropdownRef}
                                    id='PersonalInformation__addressState'
                                    name='state'
                                    mixed
                                    inputClass='PersonalInformation__input'
                                    onBlurCallback={() => {
                                        checkCommercial();
                                        checkAptUnitPoBox(personalInfoForm.mailingAptUnit.value ?? '', 'mailing');
                                    }}
                                    value={personalInfoForm.mailingState.value}
                                    setValueCallback={(value: string) => {
                                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingState: { value: value, valid: true } }));
                                    }}
                                    label='State'
                                    error={!personalInfoForm.mailingState.valid}
                                    errorText={generateFieldErrorMessage('State')}
                                    invalidErrorMessage={generateFieldErrorMessage('State', 'missingValue')}
                                    abbreviation={false}
                                    required
                                    alternate
                                    useSuccessIcon
                                />
                            </div>
                            <ZipCodeField
                                ref={zipRef}
                                className='PersonalInformation__inputContainer block'
                                id='PersonalInformation__addressZip'
                                inputClass='PersonalInformation__input'
                                onBlurCallback={() => {
                                    if (personalInfoForm.mailingZip.value.length === 5) {
                                        checkCommercial();
                                        checkAptUnitPoBox(personalInfoForm.mailingAptUnit.value ?? '', 'mailing');
                                    }
                                }}
                                value={personalInfoForm.mailingZip.value}
                                setValueCallback={(value: string) => {
                                    setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, mailingZip: { value: value, valid: true } }));
                                }}
                                label='ZIP Code'
                                error={!personalInfoForm.mailingZip.valid}
                                requiredMessage={generateFieldErrorMessage('ZIP Code', 'missingValue')}
                                errorText={generateFieldErrorMessage('ZIP Code')}
                                invalidErrorMessage={generateFieldErrorMessage('ZIP Code')}
                                required={true}
                                alternate
                                useSuccessIcon
                            />
                            {personalInfoForm.homeAddressShown && (
                                <React.Fragment>
                                    <p className='PersonalInformation__homeAddressNote'>
                                        {ReactHtmlParser(props.homeAddressNote)}
                                    </p>
                                    <AddressField
                                        addressType='home'
                                        ref={homeAddressRef}
                                        className='PersonalInformation__inputContainer block'
                                        id='PersonalInformation__homeAddressLine1'
                                        inputClass='PersonalInformation__input'
                                        value={personalInfoForm.homeAddress.value}
                                        setValueCallback={(value: string) => {
                                            const containsInvalidCharacters = regexContainer.disallow.invalid_address_characters.test(value);

                                            if (containsInvalidCharacters) {
                                                value = value.replace(regexContainer.disallow.invalid_address_characters, '');
                                            }

                                            const matchesAddress = personalInfoForm.homeAptUnit.value.toLowerCase() === value.toLowerCase();
                                            const addressFieldsAreBlank = personalInfoForm.homeAptUnit.value === '' && value === '';

                                            if (matchesAddress && !addressFieldsAreBlank) {
                                                setPersonalInfoForm((personalInfoForm: any) => ({
                                                    ...personalInfoForm,
                                                    homeAptUnit: { value: '', valid: true },
                                                    homeAddress: { value: '', valid: false },
                                                    homeCity: { value: '', valid: false },
                                                    homeState: { value: '', valid: false },
                                                    homeZip: { value: '', valid: false },
                                                }));
                                            } else {
                                                handleHomeAddressUpdate('addressLineOne', value);
                                            }
                                        }}
                                        label='Home Address'
                                        valid={[personalInfoForm.homeAddress.valid, (val: boolean) => {
                                            setPersonalInfoForm((prev: any) => ({ ...prev, homeAddress: { value: prev.homeAddress.value, valid: val } }));
                                            setPersonalInfoErrors((prev: any) => ({ ...prev, HomeAddressLine1: val ? 'None' : 'RequiredField' }));
                                        }]}
                                        error={!personalInfoForm.homeAddress.valid}
                                        requiredMessage={generateFieldErrorMessage('Home Address', 'missingValue')}
                                        errorText={generateFieldErrorMessage('Home Address', 'missingValue')}
                                        invalidErrorMessage={generateFieldErrorMessage('Home Address', 'thirdParty', 'address')}
                                        required={true}
                                        onBlurCallback={() => {
                                            checkHome();
                                        }}
                                        useOutsideValidation={personalInfoForm.homeAddress.valid}
                                        alternate
                                        useSuccessIcon
                                    />
                                    <ApartmentField
                                        className='PersonalInformation__inputContainer block'
                                        id='PersonalInformation__homeAddressLine2'
                                        inputClass='PersonalInformation__input'
                                        value={personalInfoForm.homeAptUnit.value}
                                        setValueCallback={(value: string) => {
                                            const containsInvalidCharacters = regexContainer.disallow.invalid_address_characters.test(value);

                                            if (containsInvalidCharacters) {
                                                value = value.replace(regexContainer.disallow.invalid_address_characters, '');
                                            }

                                            const matchesAddress = personalInfoForm.homeAddress.value.toLowerCase() === value.toLowerCase();
                                            const addressFieldsAreBlank = personalInfoForm.homeAddress.value === '' && value === '';

                                            if (matchesAddress && !addressFieldsAreBlank) {
                                                setPersonalInfoForm((personalInfoForm: any) => ({
                                                    ...personalInfoForm,
                                                    homeAptUnit: { value: '', valid: true },
                                                    homeAddress: { value: '', valid: false },
                                                    homeCity: { value: '', valid: false },
                                                    homeState: { value: '', valid: false },
                                                    homeZip: { value: '', valid: false },
                                                }));
                                            } else {
                                                handleHomeAddressUpdate('addressLineTwo', value);
                                            }
                                        }}
                                        label='Home Apt/Unit #'
                                        onBlurCallback={() => {
                                            checkHome();
                                            checkAptUnitPoBox(personalInfoForm.homeAptUnit.value ?? '', 'home');
                                        }}
                                        useOutsideValidation={personalInfoForm.homeAptUnit.value !== '' ? true : false}
                                        alternate
                                        addressType='home'
                                        useSuccessIcon
                                    />
                                    <CityField
                                        ref={homeCityRef}
                                        className='PersonalInformation__inputContainer block'
                                        id='PersonalInformation__homeAddressCity'
                                        inputClass='PersonalInformation__input'
                                        value={personalInfoForm.homeCity.value}
                                        setValueCallback={(newVal: string) => handleHomeAddressUpdate('city', newVal)}
                                        label='Home City'
                                        valid={[personalInfoForm.homeCity.valid, (val: boolean) => {
                                            setPersonalInfoForm((prev: any) => ({ ...prev, homeCity: { value: prev.homeCity.value, valid: val } }));
                                            setPersonalInfoErrors((prev: any) => ({ ...prev, HomeCity: val ? 'None' : 'RequiredField' }));
                                        }]}
                                        error={!personalInfoForm.homeCity.valid}
                                        requiredMessage={generateFieldErrorMessage('Home Address City', 'missingValue')}
                                        errorText={generateFieldErrorMessage('Home Address City', 'missingValue')}
                                        invalidErrorMessage={generateFieldErrorMessage('Home Address City')}
                                        required={true}
                                        onBlurCallback={() => {
                                            checkHome();
                                        }}
                                        useOutsideValidation={personalInfoForm.homeCity.valid}
                                        alternate
                                        useSuccessIcon
                                    />
                                    <div className='PersonalInformation__inputContainer PersonalInformation__dropdDown'>
                                        <StateField
                                            ref={homeStateDropdownRef}
                                            id='PersonalInformation__homeAddressState'
                                            name='state'
                                            inputClass='PersonalInformation__input'
                                            value={personalInfoForm.homeState.value}
                                            setValueCallback={(newVal: string) => {
                                                handleHomeState(newVal);
                                            }}
                                            label='Home State'
                                            abbreviation={false}
                                            required
                                            valid={[personalInfoForm.homeState.valid, (val: boolean) => {
                                                setPersonalInfoForm((prev: any) => ({ ...prev, homeState: { value: prev.homeState.value, valid: val } }));
                                                setPersonalInfoErrors((prev: any) => ({ ...prev, HomeState: val ? 'None' : 'RequiredField' }));
                                            }]}
                                            error={!personalInfoForm.homeState.valid}
                                            errorText={generateFieldErrorMessage('Home Address State')}
                                            invalidErrorMessage={generateFieldErrorMessage('Home Address State', 'missingValue')}
                                            onBlurCallback={() => {
                                                checkHome();
                                            }}
                                            useOutsideValidation={personalInfoForm.homeState.valid}
                                            alternate
                                            mixed
                                            useSuccessIcon
                                            isPrefill
                                        />
                                    </div>
                                    <ZipCodeField
                                        ref={homeZipRef}
                                        className='PersonalInformation__inputContainer block'
                                        id='PersonalInformation__homeAddressZip'
                                        inputClass='PersonalInformation__input'
                                        value={personalInfoForm.homeZip.value}
                                        setValueCallback={(newVal: string) => handleHomeAddressUpdate('zip', newVal)}
                                        label='Home ZIP Code'
                                        valid={[personalInfoForm.homeZip.valid, (val: boolean) => {
                                            setPersonalInfoForm((prev: any) => ({ ...prev, homeZip: { value: prev.homeZip.value, valid: val } }));
                                            setPersonalInfoErrors((prev: any) => ({ ...prev, HomeZipCode: val ? 'None' : 'RequiredField' }));
                                        }]}
                                        error={!personalInfoForm.homeZip.valid}
                                        requiredMessage={generateFieldErrorMessage('Home Address ZIP Code', 'missingValue')}
                                        errorText={generateFieldErrorMessage('Home Address ZIP Code')}
                                        invalidErrorMessage={generateFieldErrorMessage('Home Address ZIP Code')}
                                        required={true}
                                        onBlurCallback={() => {
                                            if (personalInfoForm.homeZip.value.length === 5) {
                                                checkHome();
                                            }
                                        }}
                                        useOutsideValidation={personalInfoForm.homeZip.valid}
                                        alternate
                                        useSuccessIcon
                                    />
                                </React.Fragment>
                            )}
                        </>
                    }
                    <div className='PersonalInformation__inputContainer'>
                        <SsnField
                            inputClass='PersonalInformation__input'
                            id='PersonalInformation__ssn'
                            label={props.ssnLabel}
                            value={personalInfoForm.ssn.value}
                            setValueCallback={(value: string) => {
                                setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, ssn: { value: value, valid: true } }));
                            }}
                            valid={[personalInfoForm.ssn.valid,
                            (val: boolean) => setPersonalInfoForm((personalInfoForm: any) => ({
                                ...personalInfoForm,
                                ['ssn']: { value: personalInfoForm.ssn.value, valid: val },
                            })),
                            ]}
                            error={!personalInfoForm.ssn.valid}
                            required
                        />
                    </div>
                    <div className='PersonalInformation__inputContainer'>
                        {getFeatureFlags('DPM-14218') ?
                            <DateOfBirthFieldWithProvider
                                label={props.dobLabel}
                                isAuthorizedUser={false}
                                helperText='MM-DD-YYYY'
                                errorMessage={generateFieldErrorMessage('Date of Birth', 'missingValue')}
                                id='PersonalInformation__dob'
                            />
                            :
                            <DateOfBirthField
                                id='PersonalInformation__dob'
                                name='dob'
                                alternate
                                helperText='MM-DD-YYYY'
                                inputClass='PersonalInformation__input--dob'
                                ctaId='ApplicationVerification--save'
                                label={props.dobLabel}
                                value={personalInfoForm.dob.value}
                                setValueCallback={(value: string) => {
                                    setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, dob: { value: value, valid: personalInfoForm.dob.valid } }));
                                }}
                                required
                                onBlurCallback={() => isSamePersonAuthorizedUserSetter()}
                                applicantStateOrAu={personalInfoForm.homeState.value || personalInfoForm.mailingState.value}
                                errorText={generateFieldErrorMessage('Date of Birth', personalInfoForm.dob.value?.length > 0 ? null : 'missingValue')}
                                useSuccessIcon
                                valid={[personalInfoForm.dob.valid,
                                (isValid: boolean) => {
                                    setPersonalInfoForm((prev) => ({
                                        ...prev,
                                        dob: { value: prev.dob.value, valid: isValid },
                                    }));
                                },
                                ]}
                            />
                        }
                    </div>
                </div>
            ) : props.partnerHostedVersion ?
                (<div className='PersonalInformation__topSectionInfo'>
                    <PartnerHostedTop />
                </div>
                )
                :
                (
                    <div className='PersonalInformation__topSectionInfo'>
                        <div className='PersonalInformation__infoContainer'>
                            <p>
                                <b>{props.nameLabel}:</b>
                            </p>
                            <p>
                                {printedName()}
                            </p>
                        </div>
                        {primaryHomeABInfo ?
                            <>
                                <div className='PersonalInformation__infoContainer PersonalInformation__infoContainer--address'>
                                    <p>
                                        <b>{props.homeAddressLabel}:</b>
                                    </p>
                                    {personalInfoForm.homeAddress.value === '' ?
                                        <>
                                            <p>
                                                {`${personalInfoForm.mailingAddress.value || ''} ${personalInfoForm.mailingAptUnit.value || ''}`}
                                            </p>
                                            <p>
                                                {`${personalInfoForm.mailingCity.value || ''}, ${personalInfoForm.mailingState.value || ''} ${personalInfoForm.mailingZip.value || ''}`}
                                            </p>
                                        </> :
                                        <>
                                            <p>
                                                {`${personalInfoForm.homeAddress.value || ''} ${personalInfoForm.homeAptUnit.value || ''}`}
                                            </p>
                                            <p>
                                                {`${personalInfoForm.homeCity.value || ''}, ${personalInfoForm.homeState.value || ''} ${personalInfoForm.homeZip.value || ''}`}
                                            </p>
                                        </>
                                    }
                                </div>
                                {personalInfoForm.homeAddress.value !== '' && personalInfoForm.mailingAddress.value !== '' && (
                                    <div className='PersonalInformation__infoContainer PersonalInformation__infoContainer--address'>
                                        <p>
                                            <b>{props.mailingAddressLabel}:</b>
                                        </p>
                                        <p>
                                            {`${personalInfoForm.mailingAddress.value || ''} ${personalInfoForm.mailingAptUnit.value || ''}`}
                                        </p>
                                        <p>
                                            {`${personalInfoForm.mailingCity.value || ''}, ${personalInfoForm.mailingState.value || ''} ${personalInfoForm.mailingZip.value || ''}`}
                                        </p>
                                    </div>
                                )}
                            </> :
                            <>
                                <div className='PersonalInformation__infoContainer PersonalInformation__infoContainer--address'>
                                    <p>
                                        <b>{props.mailingAddressLabel}:</b>
                                    </p>
                                    <p>
                                        {`${personalInfoForm.mailingAddress.value || ''} ${personalInfoForm.mailingAptUnit.value || ''}`}
                                    </p>
                                    <p>
                                        {`${personalInfoForm.mailingCity.value || ''}, ${personalInfoForm.mailingState.value || ''} ${personalInfoForm.mailingZip.value || ''}`}
                                    </p>
                                </div>
                                {personalInfoForm.homeAddressShown && (
                                    <div className='PersonalInformation__infoContainer PersonalInformation__infoContainer--address'>
                                        <p>
                                            <b>{props.homeAddressLabel}:</b>
                                        </p>
                                        <p>
                                            {`${personalInfoForm.homeAddress.value || ''} ${personalInfoForm.homeAptUnit.value || ''}`}
                                        </p>
                                        <p>
                                            {`${personalInfoForm.homeCity.value || ''}, ${personalInfoForm.homeState.value || ''} ${personalInfoForm.homeZip.value || ''}`}
                                        </p>
                                    </div>
                                )}
                            </>
                        }

                        <div className='PersonalInformation__infoContainer'>
                            <p>
                                <b>{props.ssnLabel}:</b>
                            </p>
                            <p>
                                {personalInfoForm.ssn.value ? `XXX-XX-${personalInfoForm.ssn.value.slice(-4)}` : ''}
                            </p>
                        </div>
                        <div className='PersonalInformation__infoContainer'>
                            <p>
                                <b>{props.dobLabel}:</b>
                            </p>
                            <p>
                                {getFormattedDob()}
                            </p>
                        </div>
                        {props.lockEmail ?
                            <div className='PersonalInformation__infoContainer'>
                                <p>
                                    <b>{props.emailLabel}:</b>
                                </p>
                                <p>
                                    {getPersonalInformation().emailAddress.value || ''}
                                </p>
                                {getAcquisitionFlow() === AcquisitionFlow.PqOfOne && props.emailDisclaimer &&
                                    <p className='mt-4 !text-[13px]'>
                                        <b>{props.emailDisclaimer}</b>
                                    </p>
                                }
                            </div>
                            : ''}
                    </div>
                )}
            <hr className='PersonalInformation__horizontalRule' />
            <Row className='PersonalInformation__topSectionWrapper mt-4'>
                <Col className='pl-8'>
                    <p className='PersonalInformation__requiredFields'>
                        {bottomInEditMode ? (
                            <React.Fragment>
                                Fields with an asterisk<sup className='ApplicationVerification__asterisk'>*</sup> are required
                            </React.Fragment>
                        ) : (
                            <React.Fragment>&nbsp;</React.Fragment>
                        )}
                    </p>
                </Col>
                <Col xs={2}
                    className='ApplicationVerification__toggleContainer ApplicationVerification__toggleContainer--personalInformation'>
                    {props.lockBottomSection ? (
                        <svg className='ApplicationVerification__lock' aria-hidden='true'
                            role='img' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'>
                            <path fill='currentColor'
                                d='M400 224h-24v-72C376 68.2 307.8 0 224 0S72 68.2 72 152v72H48c-26.5 0-48 21.5-48 48v192c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V272c0-26.5-21.5-48-48-48zm-104 0H152v-72c0-39.7 32.3-72 72-72s72 32.3 72 72v72z'></path>
                        </svg>
                    ) : (
                        <a className='ApplicationVerification__editToggle bottomSection' id='bottomSectionEdit'
                            onClick={handleClickEdit}>{bottomInEditMode ? 'SAVE' : 'EDIT'}</a>
                    )}
                </Col>
            </Row>


            {props.partnerHostedVersion ? (
                bottomInEditMode ? (
                    <div className='PersonalInformation__bottomSectionForm'>
                        <div className='PersonalInformation__inputContainer'>
                            <InputSocialSecurity
                                ref={ssnRef}
                                type='password'
                                id='PersonalInformation__ssn'
                                inputClass='PersonalInformation__input'
                                value={ssn}
                                setValue={setSsn}
                                label={props.ssnLabel}
                                valid={[ssnValid, setSsnValid]}
                                requiredMessage={generateFieldErrorMessage('Social Security Number', 'missingValue')}
                                errorText={generateFieldErrorMessage('Social Security Number')}
                                invalidErrorMessage={generateFieldErrorMessage('Social Security Number')}
                                required={true}
                                onChange={() => {
                                    if (ssn.length !== 0) {
                                        setSsnValid(true);
                                    }
                                }}
                                onFocus={() => setSsnValid(true)}
                                onBlur={handleBlurSsn}
                                error={!ssnValid}
                                onPaste={(e: any) => {
                                    e.preventDefault();
                                }}
                                onInput={(e: any) => {
                                    if (e.data && e.data.length > 1) {
                                        setSsn('');
                                    }
                                }}
                                alternate
                                useSuccessIcon
                                showSuccessIcon={isInputBlurred.ssn && ssnValid}
                            />
                        </div>

                    </div>
                ) : (
                    <div className='PersonalInformation__bottomSectionInfo'>
                        <div className='PersonalInformation__infoContainer'>
                            <p>
                                <b>{props.ssnLabel}:</b>
                            </p>
                            <p>
                                {ssn ? `XXX-XX-${personalInfoForm.ssn.value.slice(-4)}` : ''}
                            </p>
                        </div>
                    </div>
                )

            ) : (
                bottomInEditMode ? (
                    <div className='PersonalInformation__bottomSectionForm'>
                        {props.lockEmail ? '' : (
                            <div className='PersonalInformation__inputContainer'>
                                {getFeatureFlags('DPM-13566') ? (
                                    <EmailFieldWithProvider
                                        fieldKey='emailAddress'
                                        name='Email Address'
                                        id='emailAddress'
                                    />
                                ) : (
                                    <EmailField
                                        id='PersonalInformation__email'
                                        inputClass='PersonalInformation__input '
                                        value={personalInfoForm.email.value}
                                        setValueCallback={(value: string) => {
                                            setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, email: { value: value, valid: personalInfoForm.email.valid } }));
                                        }}
                                        onBlurCallback={async () => {
                                            setShowIcon(await validateEmail());
                                        }}
                                        showIcon={showIcon}
                                        error={!personalInfoForm.email.valid}
                                        label={props.emailLabel}
                                        useSuccessIcon
                                        alternate
                                    />
                                )}
                            </div>
                        )}
                        <div className='PersonalInformation__inputContainer'>
                            {getFeatureFlags('DPM-14217') ? (
                                <PhoneNumberFieldWithProvider
                                    fieldKey = 'phoneNumber'
                                    name = 'Phone Number'
                                    label={props.phoneLabel}
                                    className = 'PersonalInformation__input'
                                />
                            ) : (
                                <PhoneNumberField
                                    inputClass='PersonalInformation__input'
                                    value={personalInfoForm.phoneNumber.value}
                                    setValueCallback={(value: string) => {
                                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, phoneNumber: { value: value, valid: true } }));
                                    }}
                                    label={props.phoneLabel}
                                    error={!personalInfoForm.phoneNumber.valid}
                                    useSuccessIcon
                                />
                            )}
                        </div>
                        <div className='PersonalInformation__inputContainer'>
                            <MonthlyIncomeField ref={incomeRef}
                                monthlyIncomeLabel='Total Monthly Income'
                                monthlyIncome={personalInfoForm.monthlyIncome.value}
                                inputClass='PersonalInformation__input pl-12'
                                handleSetValue={(value: string) => {
                                    setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, monthlyIncome: { ...personalInfoForm.monthlyIncome, value: value } }));
                                }}
                                handleIsValidCallback={(isValid: boolean) => {
                                    setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, monthlyIncome: { value: personalInfoForm.monthlyIncome.value, valid: isValid } }));
                                }}
                                isIncomeCalculator={false}
                                isGrossIncomeVariant={false}
                            />
                        </div>
                    </div>
                ) : (
                    <div className='PersonalInformation__bottomSectionInfo'>
                        <div className='PersonalInformation__infoContainer'>
                            {props.lockEmail ? '' :
                                <div className='PersonalInformation__infoContainer'>
                                    <p>
                                        <b>{props.emailLabel}:</b>
                                    </p>
                                    <p>
                                        {getPersonalInformation().emailAddress.value || ''}
                                    </p>
                                    {getAcquisitionFlow() === AcquisitionFlow.PqOfOne && props.emailDisclaimer &&
                                        <p className='mt-4 !text-[13px]'>
                                            <b>{props.emailDisclaimer}</b>
                                        </p>
                                    }
                                </div>
                            }
                            <p>
                                <b>{props.phoneLabel}:</b>
                            </p>
                            <p>
                                {formatPhoneNumber(getPersonalInformation().phoneNumber.value)}
                            </p>
                        </div>
                        <div className='PersonalInformation__infoContainer'>
                            <p>
                                <b>{props.incomeLabel}:</b>
                            </p>
                            <p>
                                ${personalInfoForm.monthlyIncome.value?.replace(/[$]/g, '')}
                            </p>
                        </div>
                    </div>
                )

            )}

        </section>
    );
});
